import {
	ApplicationMode,
	type ConstrainedTicker,
	ConstraintRelation,
	ConstraintValidity,
	Currencies,
	InstrumentsClassificationsControllerV1ApiFactory,
	type InvestmentCreationConfigurationControllerV4Api,
	InvestmentCreationConfigurationControllerV4ApiFactory,
	type InvestmentDraftConfigurationControllerV4Api,
	InvestmentDraftConfigurationControllerV4ApiFactory,
	type InvestmentEnhancementConfigurationControllerV4Api,
	InvestmentEnhancementConfigurationControllerV4ApiFactory,
	InvestmentsStaticConfigurationControllerApiFactory,
	type RichTicker,
	type SelectableAllocationConstraintsResponse,
	TagType,
	type UserInstrumentClassificationDto,
} from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	ActionText,
	AutoTooltip,
	Banner,
	Icon,
	ScrollWrapper,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import {
	type ClassList,
	generateUniqueDOMId,
	type NodeOrFn,
	renderNodeOrFn,
	toClassName,
} from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { alwaysArray, type MaybeArray, noop, switchExpr, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { Checkbox, NumberInput, Select } from "@mdotm/shadcn/mdotm/components";
import { ForEach, useMandatoryContext } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import equal from "fast-deep-equal";
import { Map, Set } from "immutable";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { z } from "zod";
import { reportPlatformError } from "$root/api/error-reporting";
import { useApiGen } from "$root/api/hooks";
import { DataDisplayOverlay } from "$root/components/DataDisplayOverlay";
import { IconWalls } from "$root/components/IconWall";
import { spawnInstrumentDialog } from "$root/functional-areas/instruments/spawn-instrument-dialog";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { Intercom } from "$root/third-party-integrations/initIntercom";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { validateZodDefinition, zExt } from "$root/third-party-integrations/zod";
import EllipsisText from "$root/ui-lib/ellipsisText";
import { TagBadge } from "$root/ui-lib/table/custom-column/tags/Tag";
import { multiRef } from "$root/utils";
import { userClassificationId } from "../classification-id";
import { AddConstraintSection, type ConstraintAdder } from "../constraints/AddConstraintSection";
import { ConstraintListSection } from "../constraints/ConstraintListSection";
import type { ConstraintSpecificColumnsProps } from "../constraints/ConstraintSpecificColumns";
import { excludeRelationOption } from "../constraints/const";
import { DefaultConstraintListItem } from "../constraints/DefaultConstraintListItem";
import {
	ConstraintRelationBlock,
	ConstraintTooltipContent,
	isInvalid,
	solveWithAiInputStyle,
} from "../constraints/PortfolioConstraintRelation";
import { EditPortfolioV4Context, type EditPortfolioV4Props } from "../EditPortfolio.shared-types";
import { useStepSync } from "../hooks";
import { makeStepToRequestMapper, responseToStepMapper } from "../requests";
import { StepBase } from "../StepBase";
import {
	type EditablePortfolioConstraint,
	type EditablePortfolioConstraintHelper,
	type EditPortfolioV4StepPayloadMap,
	enumInstrumentWithouthScorePolicy,
	type InvestableUniverseStep,
	type PortfolioConstraintTypePayloadMap,
	type StepPropsFor,
	useHandleSubmitToCustomSubmitHandlerInContext,
} from "../shared";
import { generateAssetClassId, spawnAssetAllocationDialog } from "../spawn-asset-allocation";
import { spawnCurrencyConstraintDialog } from "../spawn-currency-constraint";
import {
	type ScoreConstraintSelectionDialogProps,
	spawnScoreConstraintSelectionDialog,
} from "../spawn-score-constraint";
import { classificationOptionColor, spawnClassificationDialog } from "../spawn-tag-classifications";

/** @internal — exported for tests */
export function instrumentGroupToLabel(list: Array<RichTicker>) {
	if (list.length === 1) {
		return list[0].instrument;
	}

	if (list.length > 1) {
		return `Selected (${list.length} instruments)`;
	}

	return "Select instruments";
}

type ConstraintCategory =
	| "assetClassConstraints"
	| "instrumentConstraints"
	| "tagConstraints"
	| "tagRelativeToTag"
	| "instrumentMaxWeightConstraint"
	| "scoresConstraint"
	| "currencyConstraints"
	| "lockInstrumentGroup"
	| "forEachConstraint"
	| "customTagConstraint"
	| "customScoreConstraint"
	| "customCurrencyConstraint"
	| "customAssetClassConstraint"
	| "assetClassGroupConstraints"
	| "customGroupAssetClassConstraints"
	| "compositionPriority";

const titleClassName = `text-[color:var(--global-palette-neutral-700)] pb-2`;

const constraintTypeToCategory: Record<keyof PortfolioConstraintTypePayloadMap, ConstraintCategory> = {
	assetClassConstraints: "assetClassConstraints",
	instrumentConstraints: "instrumentConstraints",
	instrumentMaxWeightConstraint: "instrumentMaxWeightConstraint",
	tagConstraints: "tagConstraints",
	tagRelativeToTag: "tagRelativeToTag",
	scoresConstraint: "scoresConstraint",
	currencyConstraints: "currencyConstraints",
	lockInstrumentGroup: "lockInstrumentGroup",
	forEachConstraint: "forEachConstraint",
	customScoreConstraint: "customScoreConstraint",
	customTagConstraint: "customTagConstraint",
	customAssetClassConstraint: "customAssetClassConstraint",
	customCurrencyConstraint: "customCurrencyConstraint",
	assetClassGroupConstraints: "assetClassGroupConstraints",
	customGroupAssetClassConstraints: "customGroupAssetClassConstraints",
	compositionPriority: "compositionPriority",
};

type ConstraintTemplateOmittedKey = "id" | "readonly" | "type";

function constraintTemplatesHelper<
	T extends {
		[K in keyof PortfolioConstraintTypePayloadMap]: Omit<
			EditablePortfolioConstraintHelper<K>,
			ConstraintTemplateOmittedKey
		>;
	},
>(
	a: T,
): {
	[K in keyof T & keyof PortfolioConstraintTypePayloadMap]: () => EditablePortfolioConstraintHelper<K>;
} {
	return Object.fromEntries(
		Object.entries(a).map(([key, value]) => [
			key,
			() =>
				({
					...value,
					id: generateUniqueDOMId(),
					type: key,
					readonly: false,
					//target: false,
				}) satisfies Record<ConstraintTemplateOmittedKey, unknown>,
		]),
	) as unknown as {
		[K in keyof T & keyof PortfolioConstraintTypePayloadMap]: () => EditablePortfolioConstraintHelper<K>;
	};
}

const constraintTemplates = constraintTemplatesHelper({
	assetClassConstraints: {
		maxWeight: null,
		minWeight: null,
		assetClassesIdentifiers: [],
		relation: ConstraintRelation.Between,
		identifier: null,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
	},
	tagConstraints: {
		maxWeight: null,
		minWeight: null,
		tagLabel: "",
		identifier: null,
		tagIdentifiers: [],
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
	},
	instrumentConstraints: {
		identifier: null,
		tickerIdentifiers: [],
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
	},
	tagRelativeToTag: {
		identifier: null,
		classificationUuid: null,
		options: [],
		relativeToClassificationUuid: null,
		relativeToOptions: [],
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
	},
	currencyConstraints: {
		identifier: null,
		validity: ConstraintValidity.Unchecked,
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		target: false,
		currencies: [],
	},
	instrumentMaxWeightConstraint: {
		identifier: null,
		weight: null,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
	},
	scoresConstraint: {
		identifier: null,
		maxWeight: null,
		minWeight: null,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		relation: ConstraintRelation.Between,
		instrumentWithouthScorePolicy: "EXCLUDE",
		target: false,
		scoreIdentifiers: null,
	},
	lockInstrumentGroup: {
		identifier: null,
		tickerIdentifiers: [],
		target: false,
		relation: ConstraintRelation.Equal, // must be always strict to equal
		validity: ConstraintValidity.Unchecked,
	},
	forEachConstraint: {
		identifier: null,
		tickerIdentifiers: [],
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		validity: ConstraintValidity.Unchecked,
		target: false,
	},
	customScoreConstraint: {
		identifier: null,
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
		applicationMode: ApplicationMode.PortfolioExposure,
		classificationUuid: null,
		standardClassification: null,
		considerNullAsZero: false,
		scoreIdentifiers: [],
	},
	customTagConstraint: {
		identifier: null,
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
		applicationMode: ApplicationMode.PortfolioExposure,
		classificationUuid: null,
		standardClassification: null,
		options: [],
		tagType: TagType.General,
	},
	customAssetClassConstraint: {
		identifier: null,
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
		applicationMode: ApplicationMode.PortfolioExposure,
		classificationUuid: null,
		standardClassification: null,
		options: [],
		tagType: TagType.AssetClass,
	},
	customCurrencyConstraint: {
		identifier: null,
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
		applicationMode: ApplicationMode.PortfolioExposure,
		classificationUuid: null,
		standardClassification: null,
		options: [],
		tagType: TagType.Currency,
	},
	assetClassGroupConstraints: {
		maxWeight: null,
		minWeight: null,
		assetClassesIdentifiers: [],
		relation: ConstraintRelation.Between,
		identifier: null,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
	},
	customGroupAssetClassConstraints: {
		identifier: null,
		maxWeight: null,
		minWeight: null,
		relation: ConstraintRelation.Between,
		suggestedMaxWeight: null,
		suggestedMinWeight: null,
		validity: ConstraintValidity.Unchecked,
		target: false,
		applicationMode: ApplicationMode.PortfolioExposure,
		standardClassification: null,
		classificationUuid: null,
		options: [],
		tagType: TagType.AssetClass,
	},
	compositionPriority: {
		identifier: null,
		validity: ConstraintValidity.Unchecked,
		// Always soft — footer checkbox is forced checked & disabled for this type.
		target: true,
	},
});

export function getPortfolioConstraintsStepData(
	createApi: Omit<InvestmentCreationConfigurationControllerV4Api, "basePath" | "axios">,
	enhanceApi: Omit<InvestmentEnhancementConfigurationControllerV4Api, "basePath" | "axios">,
	draftApi: Omit<InvestmentDraftConfigurationControllerV4Api, "basePath" | "axios">,
	area: EditPortfolioV4Props["area"],
): Promise<EditPortfolioV4StepPayloadMap["portfolioConstraints"]> {
	return (
		!area.portfolioUid
			? createApi.getCreationConfigurationAllocationConstraints()
			: area.name === "draft"
				? draftApi.getDraftConfigurationAllocationConstraints(area.portfolioUid)
				: enhanceApi.getEnhancementConfigurationAllocationConstraints(area.portfolioUid)
	).then(({ data }) => responseToStepMapper.portfolioConstraints(data, area));
}

const commonConstraintParams = {
	suggestedMaxWeight: z.number().nullable(),
	suggestedMinWeight: z.number().nullable(),
	target: z.boolean(),
	validity: z.nativeEnum(ConstraintValidity).optional(),
};

const commonRelationConstraint = (variant: "percentage" | "number" = "percentage") => {
	const weight = variant === "percentage" ? zExt.percentage() : z.number({ invalid_type_error: "Select a value" });
	return {
		MIN: {
			relation: z.literal(ConstraintRelation.Min),
			maxWeight: z.number().nullable().optional(),
			minWeight: weight,
		},
		MAX: {
			relation: z.literal(ConstraintRelation.Max),
			maxWeight: weight,
			minWeight: z.number().nullable().optional(),
		},
		EQUAL: {
			relation: z.literal(ConstraintRelation.Equal),
			maxWeight: z.number().nullable().optional(),
			minWeight: weight,
		},
		BETWEEN: {
			relation: z.literal(ConstraintRelation.Between),
			maxWeight: weight,
			minWeight: weight,
		},
		EXCLUDE: {
			relation: z.literal(ConstraintRelation.Exclude),
			maxWeight: z.number().nullable().optional(),
			minWeight: z.number().nullable().optional(),
		},
		RELATIVE_TO_REFERENCE: {
			relation: z.literal(ConstraintRelation.RelativeToReference),
			maxWeight: z
				.number({ invalid_type_error: "Select a value between -100 and 100" })
				.min(-100, { message: "Select a value between -100 and 100" })
				.max(100, { message: "Select a value between -100 and 100" }),
			minWeight: z
				.number({ invalid_type_error: "Select a value between -100 and 100" })
				.min(-100, { message: "Select a value between -100 and 100" })
				.max(100, { message: "Select a value between -100 and 100" }),
		},
	} as const;
};

function relationValidator(variant: "percentage" | "number" = "percentage") {
	const commonConstraintMap = commonRelationConstraint(variant);
	return z.discriminatedUnion("relation", [
		z.object({ ...commonConstraintMap.MIN }),
		z.object({ ...commonConstraintMap.MAX }),
		z.object({ ...commonConstraintMap.EQUAL }),
		z.object({ ...commonConstraintMap.EXCLUDE }),
		z.object({ ...commonConstraintMap.BETWEEN }),
		z.object({ ...commonConstraintMap.RELATIVE_TO_REFERENCE }),
	]);
}

const validations = {
	assetClassConstraints: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			assetClassesIdentifiers: z.array(z.any()).nonempty({ message: "Select an Asset Class" }),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["assetClassConstraints"], unknown>>)
		.and(relationValidator()),
	instrumentConstraints: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			tickerIdentifiers: z.array(z.any()).nonempty({ message: "Select an Instrument" }),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["instrumentConstraints"], unknown>>)
		.and(relationValidator()),
	tagRelativeToTag: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			classificationUuid: zExt.maybe(z.string()),
			options: z.array(z.string()).nonempty({ message: "Select a tag" }),
			relativeToClassificationUuid: zExt.maybe(z.string()),
			relativeToOptions: z.array(z.string()).nonempty({ message: "Select a tag" }),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["tagRelativeToTag"], unknown>>)
		.and(relationValidator()),
	instrumentMaxWeightConstraint: z.object({
		identifier: zExt.maybe(z.string()),
		target: z.boolean(),
		weight: zExt.percentage(),
		validity: z.any(),
		suggestedMinWeight: z.any(),
		suggestedMaxWeight: z.any(),
	} satisfies Record<keyof PortfolioConstraintTypePayloadMap["instrumentMaxWeightConstraint"], unknown>),
	tagConstraints: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			tagIdentifiers: z.array(z.any()).nonempty({ message: "Select a Tag" }),
			tagLabel: z.string(),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["tagConstraints"], unknown>>)
		.and(relationValidator()),
	scoresConstraint: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			instrumentWithouthScorePolicy: z.any(),
			scoreIdentifiers: z.array(z.string()),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["scoresConstraint"], unknown>>)
		.and(relationValidator("number")),
	currencyConstraints: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			currencies: z
				.array(z.nativeEnum(Currencies), {
					required_error: "Select a currency",
				})
				.min(1, { message: "Select a currency" }),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["currencyConstraints"], unknown>>)
		.and(relationValidator()),
	lockInstrumentGroup: z.object({
		identifier: zExt.maybe(z.string()),
		tickerIdentifiers: z.array(z.any()).nonempty({ message: "Select an Instrument" }),
		target: z.boolean(),
		validity: z.any(),
		relation: z.string(),
	} satisfies Record<keyof PortfolioConstraintTypePayloadMap["lockInstrumentGroup"], unknown>),
	forEachConstraint: z
		.object({
			...commonConstraintParams,
			suggestedMaxWeight: commonConstraintParams.suggestedMaxWeight.optional(),
			suggestedMinWeight: commonConstraintParams.suggestedMinWeight.optional(),
			identifier: zExt.maybe(z.string()),
			tickerIdentifiers: z.array(z.any()).nonempty({ message: "Select an Instrument" }),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["forEachConstraint"], unknown>>)
		.and(relationValidator()),
	customScoreConstraint: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			applicationMode: z.string() /** z.nativeEnum(ApplicationMode) */,
			classificationUuid: z.string({
				invalid_type_error: "Select a score",
				required_error: "Select a score",
			}),
			standardClassification: zExt.maybe(z.string()),
			considerNullAsZero: z.boolean(),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["customScoreConstraint"], unknown>>)
		.and(relationValidator("number")),
	customTagConstraint: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			applicationMode: z.string() /** z.nativeEnum(ApplicationMode) */,
			classificationUuid: z.string(),
			standardClassification: zExt.maybe(z.string()),
			options: z.array(z.string()).nonempty({ message: "Select a Tag" }),
			tagType: z.string() /** z.nativeEnum(TagType) */,
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["customTagConstraint"], unknown>>)
		.and(relationValidator()),
	customCurrencyConstraint: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			applicationMode: z.string() /** z.nativeEnum(ApplicationMode) */,
			classificationUuid: z.string(),
			standardClassification: zExt.maybe(z.string()),
			options: z.array(z.string()).nonempty({ message: "Select a Currency" }),
			tagType: z.string() /** z.nativeEnum(TagType) */,
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["customCurrencyConstraint"], unknown>>)
		.and(relationValidator()),
	customAssetClassConstraint: z
		.object({
			...commonConstraintParams,
			identifier: zExt.maybe(z.string()),
			applicationMode: z.string() /** z.nativeEnum(ApplicationMode) */,
			classificationUuid: z.string(),
			standardClassification: zExt.maybe(z.string()),
			options: z.array(z.string()).nonempty({ message: "Select an Asset Class" }),
			tagType: z.string() /** z.nativeEnum(TagType) */,
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["customAssetClassConstraint"], unknown>>)
		.and(relationValidator()),
	assetClassGroupConstraints: z
		.object({
			assetClassesIdentifiers: z.array(z.any()).nonempty({ message: "Select an Asset Class" }),
			identifier: zExt.maybe(z.string()),
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["assetClassGroupConstraints"], unknown>>)
		.and(relationValidator()),
	customGroupAssetClassConstraints: z
		.object({
			identifier: zExt.maybe(z.string()),
			applicationMode: z.string() /** z.nativeEnum(ApplicationMode) */,
			classificationUuid: z.string(),
			standardClassification: zExt.maybe(z.string()),
			options: z.array(z.string()).nonempty({ message: "Select a Tag" }),
			tagType: z.string() /** z.nativeEnum(TagType) */,
		} satisfies Partial<Record<keyof PortfolioConstraintTypePayloadMap["customGroupAssetClassConstraints"], unknown>>)
		.and(relationValidator()),
	compositionPriority: z.object({
		identifier: zExt.maybe(z.string()),
		target: z.boolean(),
		validity: z.any(),
	} satisfies Record<keyof PortfolioConstraintTypePayloadMap["compositionPriority"], unknown>),
} satisfies Record<keyof PortfolioConstraintTypePayloadMap, unknown>;

const resolver = zodResolver(
	z.object({
		list: z.array(
			z
				.discriminatedUnion("type", [
					z.object({ type: z.literal("assetClassConstraints") }).passthrough(),
					z.object({ type: z.literal("instrumentConstraints") }).passthrough(),
					z.object({ type: z.literal("instrumentMaxWeightConstraint") }).passthrough(),
					z.object({ type: z.literal("tagConstraints") }).passthrough(),
					z.object({ type: z.literal("tagRelativeToTag") }).passthrough(),
					z.object({ type: z.literal("scoresConstraint") }).passthrough(),
					z.object({ type: z.literal("currencyConstraints") }).passthrough(),
					z.object({ type: z.literal("lockInstrumentGroup") }).passthrough(),
					z.object({ type: z.literal("forEachConstraint") }).passthrough(),
					z.object({ type: z.literal("customScoreConstraint") }).passthrough(),
					z.object({ type: z.literal("customTagConstraint") }).passthrough(),
					z.object({ type: z.literal("customAssetClassConstraint") }).passthrough(),
					z.object({ type: z.literal("customCurrencyConstraint") }).passthrough(),
					z.object({ type: z.literal("assetClassGroupConstraints") }).passthrough(),
					z.object({ type: z.literal("customGroupAssetClassConstraints") }).passthrough(),
					z.object({ type: z.literal("compositionPriority") }).passthrough(),
				])
				.superRefine((item, ctx) => {
					switch (item.type) {
						case "assetClassConstraints":
							validateZodDefinition(validations.assetClassConstraints)(item, ctx);
							break;
						case "instrumentConstraints":
							validateZodDefinition(validations.instrumentConstraints)(item, ctx);
							break;
						case "tagConstraints":
							validateZodDefinition(validations.tagConstraints)(item, ctx);
							break;
						case "tagRelativeToTag":
							validateZodDefinition(validations.tagRelativeToTag)(item, ctx);
							break;
						case "currencyConstraints":
							validateZodDefinition(validations.currencyConstraints)(item, ctx);
							break;
						case "scoresConstraint":
							validateZodDefinition(validations.scoresConstraint)(item, ctx);
							break;
						case "customTagConstraint":
							validateZodDefinition(validations.customTagConstraint)(item, ctx);
							break;
						case "customGroupAssetClassConstraints":
							validateZodDefinition(validations.customGroupAssetClassConstraints)(item, ctx);
							break;
						case "customAssetClassConstraint":
							validateZodDefinition(validations.customAssetClassConstraint)(item, ctx);
							break;
						case "customCurrencyConstraint":
							validateZodDefinition(validations.customCurrencyConstraint)(item, ctx);
							break;
						case "forEachConstraint":
							validateZodDefinition(validations.forEachConstraint)(item, ctx);
							break;
						case "assetClassGroupConstraints":
							validateZodDefinition(validations.assetClassGroupConstraints)(item, ctx);
							break;
						case "customScoreConstraint":
							validateZodDefinition(validations.customScoreConstraint)(item, ctx);
							break;
						case "instrumentMaxWeightConstraint":
							validateZodDefinition(validations.instrumentMaxWeightConstraint)(item, ctx);
							break;
						case "lockInstrumentGroup":
							validateZodDefinition(validations.lockInstrumentGroup)(item, ctx);
							break;
						case "compositionPriority":
							validateZodDefinition(validations.compositionPriority)(item, ctx);
							break;
					}
				})
				.refine(
					(item) => {
						switch (item.type) {
							case "customScoreConstraint":
							case "assetClassConstraints":
							case "instrumentConstraints":
							case "tagRelativeToTag":
							case "customTagConstraint":
							case "customGroupAssetClassConstraints":
							case "customAssetClassConstraint":
							case "customCurrencyConstraint":
							case "forEachConstraint":
							case "assetClassGroupConstraints":
							case "currencyConstraints":
								return item.relation === "BETWEEN" &&
									typeof item.minWeight === "number" &&
									typeof item.maxWeight === "number"
									? item.minWeight <= item.maxWeight
									: true;
							default:
								return true;
						}
					},
					{
						message: "Min should be lower than Max",
						path: ["minWeight"],
					},
				),
		),
	}),
);
export function Step03_PortfolioConstraints({
	context,
	stepMetadata,
	stepData,
	onStepDataChange,
	onStepError,
	toggleDirty,
	allStepsData,
}: StepPropsFor<"portfolioConstraints">): React.JSX.Element {
	const { saveHandlers, area } = context;
	const edit = area.editable && area.edit;

	const [revertData, setRevertData] = useState<Map<string, (typeof stepData)["list"][number]>>(() => Map());
	const [solutionsWithAiApplied, setSolutionsWithAiApplied] = useState<Set<string>>(() => Set());
	const [aiApplied, setAiApplied] = useState(false);

	const createInvestmentApi = useApiGen(InvestmentCreationConfigurationControllerV4ApiFactory);
	const draftInvestmentApi = useApiGen(InvestmentDraftConfigurationControllerV4ApiFactory);
	const enhanceInvestmentApi = useApiGen(InvestmentEnhancementConfigurationControllerV4ApiFactory);
	const staticController = useApiGen(InvestmentsStaticConfigurationControllerApiFactory);
	const instrumentsClassificationsControllerV1Api = useApiGen(InstrumentsClassificationsControllerV1ApiFactory);

	const form = useForm({
		defaultValues: stepData,
		resolver,
		mode: "onBlur",
		shouldFocusError: false,
	});

	const { control, handleSubmit, reset, getValues, watch, trigger, formState } = form;
	const { replace, append } = useFieldArray({
		control,
		name: "list",
		keyName: "_id",
	});
	const observableList = watch("list");
	const isCalculating = stepMetadata.processing;

	const applyAllAISolutions = () => {
		const invalidEntries = getValues().list.filter((x) => x.validity === ConstraintValidity.Invalid);
		const newRevertData = Map(invalidEntries.map((x) => [x.id, x]));
		setRevertData(() => newRevertData);
		replace(
			getValues().list.flatMap((x) => {
				if (newRevertData.has(x.id)) {
					const solutions = alwaysArray(applyAISolution(newRevertData.get(x.id)!));
					setSolutionsWithAiApplied((prev) => prev.union(solutions.map((s) => s.id)));
					return solutions;
				}

				return [x];
			}),
		);
		if (invalidEntries.length > 0) {
			setAiApplied(true);
		} else {
			setAiApplied(false);
		}
		form.trigger("list");
	};

	const revertAllAISolutions = useCallback(() => {
		const currentList = getValues().list;
		const revertDataKeys = new globalThis.Set([...revertData.keys()]);
		const aiAddedIds = new globalThis.Set([...solutionsWithAiApplied].filter((id) => !revertDataKeys.has(id)));
		replace(
			currentList.filter((x) => !aiAddedIds.has(x.id)).map((x) => (revertData.has(x.id) ? revertData.get(x.id)! : x)),
		);
		setSolutionsWithAiApplied(() => Set());
		setRevertData(() => Map());
		setAiApplied(false);
		form.clearErrors("list");
	}, [getValues, replace, revertData, solutionsWithAiApplied, form]);

	useEffect(() => {
		if (area.name !== "settings-current" && area.name !== "settings-enhanced") {
			noop(stepData);
			setSolutionsWithAiApplied(() => Set());
			setRevertData(() => Map());
			setAiApplied(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepData, area.name]);

	useStepSync({ reset, toggleDirty, isDirty: formState.isDirty, stepData });

	const { data: selectablePortfolioConstraints } = useQueryNoRefetch(
		["getStepAllocationConstrainsData", stepMetadata.processing],
		{
			enabled: !stepMetadata.processing,
			queryFn: () => {
				return match(context)
					.with({ area: { name: "enhance" } }, (x) =>
						axiosExtract(
							enhanceInvestmentApi.getEnhancementConfigurationSelectableAllocationConstraints(x.area.portfolioUid),
						),
					)
					.with({ area: { name: "settings-enhanced" } }, (x) =>
						axiosExtract(
							staticController.getStaticConfigurationSelectableAllocationConstraints(
								x.area.portfolioUid,
								allStepsData.investableUniverse.universeIdentifier,
								allStepsData.investableUniverse.type,
							),
						),
					)
					.with({ area: { name: "settings-current" } }, (x) =>
						axiosExtract(
							staticController.getStaticConfigurationSelectableAllocationConstraints(
								x.area.portfolioUid,
								allStepsData.investableUniverse.universeIdentifier,
								allStepsData.investableUniverse.type,
							),
						),
					)
					.with({ area: { name: "create" } }, () =>
						axiosExtract(createInvestmentApi.getCreationConfigurationSelectableAllocationConstraints()),
					)
					.with({ area: { name: "draft" } }, (x) =>
						axiosExtract(draftInvestmentApi.getDraftConfigurationSelectableAllocationConstraints(x.area.portfolioUid)),
					)
					.exhaustive();
			},
		},
	);

	const { data: selectableUserClassificationResponse } = useQueryNoRefetch(["querySelectableCustomTags"], {
		enabled: !stepMetadata.processing,
		queryFn() {
			return axiosExtract(instrumentsClassificationsControllerV1Api.retrieveAllClassifications());
		},
	});

	const availableClassificationCustomTags = useMemo(
		() => selectableUserClassificationResponse?.filter((x) => x.fieldType === "TAG" && x.tagType === "GENERAL") ?? [],
		[selectableUserClassificationResponse],
	);

	const selectableUserScoreClassification = selectableUserClassificationResponse?.filter(
		(selectable) =>
			!observableList.some((row) => {
				if (row.type === "customScoreConstraint") {
					return row.classificationUuid === selectable.classificationUuid;
				}

				return false;
			}),
	);

	// customTagConstraint add overload concept to current constraint
	// BE should add a layer, so we can have different constraint based on type and not discriminate them based of subtype
	const constraintAdders = useMemo<Array<ConstraintAdder<keyof PortfolioConstraintTypePayloadMap>>>(
		() => [
			{
				label: "Asset allocation",
				type: "assetClassConstraints",
				disabled: isCalculating || !allStepsData["investableUniverse"].universeIdentifier,
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already selected all Asset allocation categories",
				status: isCalculating ? "calculating" : undefined,
			},
			{
				label: "Group asset allocation",
				type: "assetClassGroupConstraints",
				disabled: isCalculating || !allStepsData["investableUniverse"].universeIdentifier,
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already selected all Asset allocation categories",
				status: isCalculating ? "calculating" : undefined,
			},
			{
				label: "General tag",
				type: "customTagConstraint",
				disabled:
					isCalculating ||
					availableClassificationCustomTags.length === 0 ||
					!allStepsData["investableUniverse"].universeIdentifier,
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already selected all Tag categories", //TODO: change
				status: isCalculating ? "calculating" : undefined,
			},
			{
				maxOfType: 1,
				label: "Max weights on single instrument",
				type: "instrumentMaxWeightConstraint",
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already added Max weights on single instrument",
				disabled: isCalculating || !allStepsData["investableUniverse"].universeIdentifier,
				status: isCalculating ? "calculating" : undefined,
			},
			{
				maxOfType: selectableUserScoreClassification?.length ?? 0,
				label: "Score & metrics",
				type: "customScoreConstraint",
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already selected all Score & Metrics categories",
				disabled:
					isCalculating ||
					!(selectableUserScoreClassification && selectableUserScoreClassification?.length > 0) ||
					!allStepsData["investableUniverse"].universeIdentifier,
				status: isCalculating ? "calculating" : undefined,
			},
			{
				label: "Currency",
				type: "currencyConstraints",
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already selected all currencies",
				disabled: isCalculating || !allStepsData["investableUniverse"].universeIdentifier,
				status: isCalculating ? "calculating" : undefined,
			},
			{
				maxOfType: 1,
				label: "Lock instrument weight",
				type: "lockInstrumentGroup",
				disabled:
					isCalculating ||
					selectablePortfolioConstraints?.selectableLockableInstruments?.length === undefined ||
					selectablePortfolioConstraints?.selectableLockableInstruments?.length === 0,
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already selected all lock instrument",
				hidden: context.area.name === "create",
				status: isCalculating ? "calculating" : undefined,
			},
			{
				label: "Single instrument",
				type: "forEachConstraint",
				disabled:
					isCalculating ||
					selectablePortfolioConstraints?.selectableInstruments?.length === undefined ||
					selectablePortfolioConstraints?.selectableInstruments?.length === 0,
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already selected all for each constraint",
				status: isCalculating ? "calculating" : undefined,
			},
			{
				label: "Group instrument",
				type: "instrumentConstraints",
				disabled:
					isCalculating ||
					selectablePortfolioConstraints?.selectableInstruments === undefined ||
					selectablePortfolioConstraints?.selectableInstruments.length === 0,
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "All the universe instruments have already been selected",
				status: isCalculating ? "calculating" : undefined,
			},
			{
				label: "Tag relative to tag",
				type: "tagRelativeToTag",
				disabled: isCalculating || !allStepsData["investableUniverse"].universeIdentifier,
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: "You have already added all tag relative to tag constraints",
				status: isCalculating ? "calculating" : undefined,
			},
			{
				maxOfType: 1,
				label: "Composition priority",
				type: "compositionPriority",
				disabled:
					isCalculating || !allStepsData["investableUniverse"].universeIdentifier || context.area.name !== "enhance",
				limitTooltip: isCalculating
					? "Sphere is calculating the constraint limits"
					: !allStepsData["investableUniverse"].universeIdentifier
						? "To add this constraint, please select a universe first."
						: context.area.name !== "enhance"
							? "Only available when creating proposals."
							: "You have already added Composition priority",
				status: isCalculating ? "calculating" : undefined,
			},
		],
		[
			selectablePortfolioConstraints?.selectableLockableInstruments?.length,
			selectablePortfolioConstraints?.selectableInstruments,
			isCalculating,
			allStepsData,
			availableClassificationCustomTags.length,
			selectableUserScoreClassification,
			context.area.name,
		],
	);

	const stepToRequestMapper = useMemo(() => makeStepToRequestMapper(area.portfolioUid), [area.portfolioUid]);

	const onSubmitAsync = useHandleSubmitToCustomSubmitHandlerInContext({
		context,
		stepName: "portfolioConstraints",
		handleSubmit,
		baseSubmitFn: async (values) => {
			try {
				onStepDataChange(await saveHandlers.portfolioConstraints(values), {
					skipMetadataUpdate: true,
					persist: true,
				});
			} catch (err) {
				onStepError();
				reportPlatformError(err, "ERROR", "portfolio-creation", {
					message: "Save portfolio constraints",
					payload: JSON.stringify(values),
				});
				throw err;
			}
		},
		onInvalid() {
			trigger("list").catch(noop);
		},
	});
	return (
		<StepBase title="Portfolio constraints" optional={stepMetadata.optional} onSubmitAsync={() => onSubmitAsync()}>
			<DataDisplayOverlay
				dataProvider={() => ({
					formData: watch(),
					requestBody: stepToRequestMapper.portfolioConstraints(watch()),
				})}
				dataSource="Portfolio Constraints"
			/>
			<div className="mb-6" data-qualifier="portfolioWizard/portfolioConstraints/hint">
				Portfolio constraints are hard or soft limits that will guide Sphere when building your portfolio. These can
				include limits on asset classes, sectors or other relevant factors. Constraints can be monitored over time using
				the dedicated widget to ensure ongoing alignment with your strategy.{" "}
				<ActionText type="Body/M/Bold" onClick={() => Intercom.showArticle("portfolioConstraints")}>
					Read more.
				</ActionText>
			</div>
			{edit && (
				<AddConstraintSection
					constraintAdders={constraintAdders}
					constraintList={observableList.map((constraint) => ({
						...constraint,
						type: remapConstraintCategory(constraint.type),
					}))}
					onAdd={(type) =>
						append({
							...constraintTemplates[type](),
							// also used in src/components/Portfolio/CreatePortfolio/constraints/PortfolioConstraintRelation.tsx
							// compositionPriority is always a soft constraint (target forced on, see the footer checkbox).
							target: type === "compositionPriority" ? true : allStepsData.investableUniverse.type === "GROUP_UNIVERSE",
						})
					}
				/>
			)}
			<div className="mb-6">
				<div className="font-bold text-[16px] mb-2">Selected constraints</div>
				{edit && (
					<div>
						Add preferred constraints and Sphere will validate them. You can also turn constraints into soft constraints
						or adjust their order by dragging them. The order determines their priority in finding an optimal solution
						for your portfolio.
					</div>
				)}
			</div>
			{!isCalculating &&
				area.name !== "settings-current" &&
				area.name !== "settings-enhanced" &&
				(aiApplied ? revertData.size > 0 : observableList.some((x) => x.validity === ConstraintValidity.Invalid)) && (
					<div className="mb-4">
						<Banner
							severity="error"
							overrides={
								aiApplied
									? {
											background: themeCSSVars.palette_graph_B50,
											iconColor: themeCSSVars.palette_graph_B500,
											icon: "Ask-ai",
										}
									: {
											background: themeCSSVars.palette_N50,
											iconColor: themeCSSVars.palette_W500,
											icon: "Icon-full-alert",
										}
							}
							title={aiApplied ? "Feasibility adjustments applied" : "Constraints need adjustment"}
						>
							<div>
								{aiApplied
									? "Constraints have been updated to the closest feasible values."
									: "Some values aren\u2019t feasible. We\u2019ve found the closest valid configuration for you."}
							</div>
							<div className="mt-1">
								{aiApplied ? (
									<ActionText
										data-qualifier="portfolioWizard/portfolioConstraints/revertAdjustments"
										onClick={() => revertAllAISolutions()}
									>
										Revert to original values
									</ActionText>
								) : (
									<ActionText
										data-qualifier="portfolioWizard/portfolioConstraints/applyAdjustments"
										onClick={() => applyAllAISolutions()}
									>
										Apply adjustments
									</ActionText>
								)}
							</div>
						</Banner>
					</div>
				)}
			{observableList.length === 0 ? (
				<div className="flex-1 min-h-0 flex flex-col">
					<IconWalls.ConstraintNotSet constraintName="portfolio constraint" />
				</div>
			) : (
				<FormProvider {...form}>
					<ConstraintListSection
						constraints={observableList}
						onChange={(constraints) => {
							replace(
								constraints.map((constraint) => ({
									...constraint,
									validity: ConstraintValidity.Unchecked,
								})),
							);
						}}
						reOrderable={edit}
						// Force remounting components when the list order or size changes. Without this, we have
						// issues with how react-hook-form handles inputs in this page.
						key={observableList.map((x) => x.id + x.validity).join(";")}
					>
						{(props) => (
							<DefaultConstraintListItem
								{...props}
								actions={edit && !isCalculating}
								disableActions={match(props.item.type)
									.returnType<Array<"EDIT" | "DELETE"> | undefined>()
									.with("tagConstraints", "scoresConstraint", () => ["EDIT"] as const)
									.otherwise(() => undefined)}
								onRemove={(id) => {
									props.onRemove(id);
									if (revertData.size > 0 && !aiApplied) {
										trigger("list").catch(noop);
									}
								}}
								subItem={
									<PortfolioConstraintSubListItem
										item={props.item}
										index={props.index}
										control={control}
										editable={edit && !isCalculating}
										solvedWithAI={solutionsWithAiApplied.has(props.item.id)}
										onConstraintChange={() => {
											setRevertData((cur) => cur.remove(props.item.id));
											setSolutionsWithAiApplied((cur) => cur.remove(props.item.id));
											replace(
												getValues().list.map((constraint) =>
													constraint.id === props.item.id
														? {
																...constraint,
																validity: ConstraintValidity.Unchecked,
															}
														: constraint,
												),
											);
										}}
									/>
								}
							>
								<PortfolioConstraintListItem
									item={props.item}
									control={control}
									index={props.index}
									selectableData={{
										...(selectablePortfolioConstraints ?? {}),
										selectableUserClassification: selectableUserClassificationResponse,
									}}
									editable={!isCalculating}
									trigger={unpromisify((fieldName) => trigger(fieldName).then(noop))}
									solvedWithAI={solutionsWithAiApplied.has(props.item.id)}
									revertData={revertData.get(props.item.id)}
									onConstraintChange={() => {
										setRevertData((cur) => cur.remove(props.item.id));
										setSolutionsWithAiApplied((cur) => cur.remove(props.item.id));
										replace(
											getValues().list.map((constraint) =>
												constraint.id === props.item.id
													? {
															...constraint,
															validity: ConstraintValidity.Unchecked,
														}
													: constraint,
											),
										);
									}}
								/>
							</DefaultConstraintListItem>
						)}
					</ConstraintListSection>
				</FormProvider>
			)}
		</StepBase>
	);
}

function RowTooltip(props: {
	solvedWithAI: boolean;
	relation: ConstraintRelation;
	minWeight?: number;
	maxWeight?: number;
	constraint: "forEachConstraint" | "lockInstrumentGroup";
}) {
	const { solvedWithAI, relation, maxWeight, minWeight } = props;
	const betweeenText = "Current instrument weight between";
	const defaultText = "Current instrument weight";
	return (
		<AutoTooltip
			severity="warning"
			position="right"
			mode="hover"
			maxWidth={400}
			overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
			trigger={({ innerRef }) => (
				<Icon
					size={16}
					icon={solvedWithAI ? "Ask-ai" : "Icon-full-alert"}
					color={solvedWithAI ? themeCSSVars.palette_graph_B500 : themeCSSVars.palette_W500}
					innerRef={innerRef}
				/>
			)}
		>
			<TooltipContent>
				<div className="flex space-x-1 mb-2 items-center pr-4">
					<Icon
						icon={solvedWithAI ? "Ask-ai" : "Icon-full-alert"}
						color={solvedWithAI ? themeCSSVars.palette_graph_B500 : themeCSSVars.palette_W500}
						size={16}
					/>
					<Text as="span" type="Body/M/Bold">
						{solvedWithAI ? "Feasibility Adjustments Applied" : "Unfeasible constraints"}
					</Text>
				</div>
				<Text as="p" type="Body/M/Book" className="mb-2">
					{solvedWithAI
						? `This instrument has been removed from the ${
								props.constraint === "lockInstrumentGroup" ? "lock constraint" : "single instrument constraint"
							}.`
						: "Remove the constraint or change the constraints order."}
				</Text>
				{switchExpr(relation, {
					BETWEEN: () => (
						<Text as="p" type="Body/M/Book" className="mb-2 italic">
							{betweeenText}: Min: <span className="not-italic">{minWeight ?? "-"}%</span> Max:{" "}
							<span className="not-italic">{maxWeight ?? "-"}%</span>
						</Text>
					),
					// TODO: replace
					RELATIVE_TO_REFERENCE: () => (
						<Text as="p" type="Body/M/Book" className="mb-2 italic">
							{betweeenText}: Min: <span className="not-italic">{minWeight ?? "-"}%</span> Max:{" "}
							<span className="not-italic">{maxWeight ?? "-"}%</span>
						</Text>
					),
					EQUAL: () => (
						<Text as="p" type="Body/M/Book" className="mb-2 italic">
							{defaultText}: <span className="not-italic">{minWeight ?? "-"}%</span>
						</Text>
					),
					MAX: () => (
						<Text as="p" type="Body/M/Book" className="mb-2 italic">
							{defaultText}: <span className="not-italic">{maxWeight ?? "-"}%</span>
						</Text>
					),
					MIN: () => (
						<Text as="p" type="Body/M/Book" className="mb-2 italic">
							{defaultText}: <span className="not-italic">{minWeight ?? "-"}%</span>
						</Text>
					),
					EXCLUDE: () => <></>,
				})}
			</TooltipContent>
		</AutoTooltip>
	);
}

function ForEachInstrumentRow(
	props: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"forEachConstraint">> & {
		editable: boolean;
		solvedWithAI: boolean;
		onConstraintChange?(): void;
	},
): React.JSX.Element {
	const forEachInstrumentHandler = useFieldArray({
		control: props.control,
		name: `list.${props.index}.tickerIdentifiers`,
		keyName: "_id",
	});

	return (
		<ScrollWrapper className="max-h-[300px] mt-4" direction="vertical">
			<ForEach collection={props.item.tickerIdentifiers}>
				{(item, index) => (
					<div
						className={toClassName({
							[`py-3 flex justify-between`]: true,
							[`border-t`]: index === 0,
							[`border-b border-b-[var(--global-palette-neutral-200)]`]:
								index !== props.item.tickerIdentifiers.length - 1,
						})}
					>
						<div className="flex">
							<div className="w-[207px]" />
							<div className="flex space-x-2 items-end">
								<p
									className={toClassName({
										[`text-[color:var(--global-palette-neutral-700)]`]: true,
										"line-through	": item.deleted,
									})}
								>
									{item.instrument}
								</p>
								{item.feasible === false && item.deleted && (
									<RowTooltip
										solvedWithAI={props.solvedWithAI}
										relation={props.item.relation ?? "EXCLUDE"}
										minWeight={item.suggestedMinWeight}
										maxWeight={item.suggestedMaxWeight}
										constraint="forEachConstraint"
									/>
								)}
							</div>
						</div>
						{props.editable && !item.deleted && (
							<button
								type="button"
								onClick={() => {
									forEachInstrumentHandler.remove(index);
									props.onConstraintChange?.();
								}}
							>
								<Icon icon="Delete" color={themeCSSVars.Button_bg_primary} size={21} />
							</button>
						)}
						{props.editable && item.deleted && (
							<button
								type="button"
								onClick={() => {
									forEachInstrumentHandler.update(index, {
										...item,
										deleted: false,
									});
									props.onConstraintChange?.();
								}}
							>
								<Icon icon="restore" color={themeCSSVars.Button_bg_primary} size={21} />
							</button>
						)}
					</div>
				)}
			</ForEach>
		</ScrollWrapper>
	);
}

function LockInstrumentRow(
	props: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"lockInstrumentGroup">> & {
		editable: boolean;
		solvedWithAI: boolean;
		onConstraintChange?(): void;
	},
): React.JSX.Element {
	const lockInstrumentHandler = useFieldArray({
		control: props.control,
		name: `list.${props.index}.tickerIdentifiers`,
		keyName: "_id",
	});

	return (
		<ScrollWrapper className="max-h-[300px] mt-4" direction="vertical">
			<ForEach collection={props.item.tickerIdentifiers}>
				{(item, index) => (
					<div
						className={toClassName({
							[`py-3 flex justify-between`]: true,
							[`border-t`]: index === 0,
							[`border-b border-b-[var(--global-palette-neutral-200)]`]:
								index !== props.item.tickerIdentifiers.length - 1,
						})}
					>
						<div className="flex">
							<div className="w-[207px]" />
							<div className="flex space-x-2 items-end">
								<p
									className={toClassName({
										[`text-[color:var(--global-palette-neutral-700)]`]: true,
										"line-through	": item.deleted,
									})}
								>
									{item.instrument}
								</p>
								{item.feasible === false && item.deleted && (
									<RowTooltip
										solvedWithAI={props.solvedWithAI}
										relation="EQUAL"
										minWeight={item.suggestedMinWeight}
										maxWeight={item.suggestedMaxWeight}
										constraint="lockInstrumentGroup"
									/>
								)}
							</div>
						</div>
						{props.editable && !item.deleted && (
							<button
								type="button"
								onClick={() => {
									lockInstrumentHandler.remove(index);
									props.onConstraintChange?.();
								}}
							>
								<Icon icon="Delete" color={themeCSSVars.Button_bg_primary} size={21} />
							</button>
						)}
						{props.editable && item.deleted && (
							<button
								type="button"
								onClick={() => {
									lockInstrumentHandler.update(index, {
										...item,
										deleted: false,
									});
									props.onConstraintChange?.();
								}}
							>
								<Icon icon="restore" color={themeCSSVars.Button_bg_primary} size={21} />
							</button>
						)}
					</div>
				)}
			</ForEach>
		</ScrollWrapper>
	);
}

function PortfolioConstraintSubListItem({
	control,
	editable,
	index,
	item,
	solvedWithAI,
	onConstraintChange,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<keyof PortfolioConstraintTypePayloadMap>> & {
	editable: boolean;
	solvedWithAI: boolean;
	onConstraintChange?(): void;
}) {
	return match(item)
		.with({ type: "lockInstrumentGroup" }, (params) => (
			<LockInstrumentRow
				item={params}
				control={
					control as ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"lockInstrumentGroup">>["control"]
				}
				index={index}
				editable={editable}
				solvedWithAI={solvedWithAI}
				onConstraintChange={onConstraintChange}
			/>
		))
		.with({ type: "forEachConstraint" }, (params) => (
			<ForEachInstrumentRow
				item={params}
				control={
					control as ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"forEachConstraint">>["control"]
				}
				index={index}
				editable={editable}
				solvedWithAI={solvedWithAI}
				onConstraintChange={onConstraintChange}
			/>
		))
		.otherwise(() => <></>);
}

//#region Asset class Constraints
function AssetAllocationColumns({
	index,
	item,
	control,
	selectableUserClassifications,
	selectableAssetClasses,
	editable,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"assetClassConstraints">> & {
	selectableUserClassifications: UserInstrumentClassificationDto[] | undefined;
	selectableAssetClasses: SelectableAllocationConstraintsResponse["selectableAssetClasses"] | undefined;
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"assetClassConstraints">;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};
	const { setValue, getValues } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();
	const { assetClassesIdentifiers: _assetClassesIdentifiers, ...common } = item;

	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>{item.assetClassesIdentifiers.at(0)?.name ? "Risk model" : "Asset class"}</div>
				<div>
					{item.readonly || !editable ? (
						<EllipsisText text={item.assetClassesIdentifiers?.[0]?.name} />
					) : (
						<FormController
							control={control}
							name={`list.${index}.assetClassesIdentifiers`}
							render={({ fieldState, field }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});

								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/assetClassesIdentifiers`}
												innerRef={innerRef}
												disabled={!selectableAssetClasses || !selectableUserClassifications}
												onClick={() =>
													selectableAssetClasses &&
													selectableUserClassifications &&
													spawnAssetAllocationDialog({
														classificationType: "RISK_MODEL",
														initialValue: {
															selectedOptions: getValues(`list.${index}.assetClassesIdentifiers`),
														},
														optionsProvider(selectedClassificationUuid) {
															return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																ctx.area,
																{
																	classificationUuid: selectedClassificationUuid,
																	universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																},
															);
														},
														disabledRowIds: getValues().list.flatMap((constraints, i) =>
															i === index // self can always be changed
																? []
																: constraints.type === "assetClassConstraints"
																	? constraints.assetClassesIdentifiers.map((assetClass) =>
																			generateAssetClassId(assetClass),
																		)
																	: constraints.type === "customAssetClassConstraint"
																		? constraints.options.map((value) =>
																				userClassificationId(constraints.classificationUuid!, value),
																			)
																		: [],
														),
														selectableAssetClasses,
														selectableUserClassifications,
														onSubmit(data) {
															if (data.type === "RISK_MODEL") {
																setValue(`list.${index}.assetClassesIdentifiers`, data.selectedOptions);
																trigger(`list.${index}.assetClassesIdentifiers`);
																onConstraintChange();
																return;
															}

															if (data.type === "TAG") {
																setValue(`list.${index}`, {
																	...common,
																	applicationMode: "PORTFOLIO_EXPOSURE",
																	options: data.selectedOptions,
																	classificationUuid: data.instrumentClassification?.classificationUuid ?? null,
																	tagType: "ASSET_CLASS",
																	standardClassification:
																		data.instrumentClassification?.standardClassificationId ?? null,
																	type: "customAssetClassConstraint",
																	suggestedMaxWeight: null,
																	suggestedMinWeight: null,
																} satisfies EditablePortfolioConstraintHelper<"customAssetClassConstraint">);
																trigger(`list.${index}.standardClassification`);
																onConstraintChange();
															}
														},
													})
												}
												className={{
													"w-full text-left": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												<EllipsisText text={item.assetClassesIdentifiers?.[0]?.name ?? "Select value"} />
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}
//#endregion

//#region Instrument Constraints
function InstrumentConstraintsColumns({
	index,
	item,
	control,
	selectableInstruments,
	editable,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"instrumentConstraints">> & {
	selectableInstruments: SelectableAllocationConstraintsResponse["selectableInstruments"];
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"instrumentConstraints">;
}) {
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};
	return (
		<>
			<div className="max-w-[180px] min-[1820px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>Instruments</div>
				{item.readonly || !editable ? (
					<EllipsisText text={instrumentGroupToLabel(item.tickerIdentifiers)} />
				) : (
					<FormController
						control={control}
						name={`list.${index}.tickerIdentifiers`}
						render={({ fieldState, field: { value: tickerIdentifiers, ref, onChange } }) => {
							const error = Boolean(fieldState.error?.message);
							const invalid = isInvalid({
								error,
								validity: item.validity,
								mode: solvedWithAI ? "ai" : "edit",
							});

							return (
								<AutoTooltip
									severity="error"
									position="left"
									mode="hover"
									disabled={!error}
									overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
									trigger={({ innerRef }) => (
										<div ref={innerRef} className="flex items-center gap-1">
											<ActionText
												disabled={!selectableInstruments}
												onClick={() =>
													selectableInstruments &&
													spawnInstrumentDialog({
														instruments: selectableInstruments,
														selected: tickerIdentifiers,
														onSubmit: (v) => {
															onChange(v);
															trigger(`list.${index}.tickerIdentifiers`);
															onConstraintChange();
														},
														rowClassName: `portfolioConstraintsTable/constraint(${item.type},${index})/row`,
													})
												}
												innerRef={ref}
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/tickerIdentifiers`}
												className={{
													"truncate py-2 h-6 leading-none!": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												{instrumentGroupToLabel(tickerIdentifiers)}
											</ActionText>
										</div>
									)}
								>
									{fieldState.error?.message}
								</AutoTooltip>
							);
						}}
					/>
				)}
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
				overrideRelationOptions={excludeRelationOption(["RELATIVE_TO_REFERENCE"])}
			/>
		</>
	);
}
//#endregion

//#region Tag relative to tag Constraints
function TagRelativeToTagColumns({
	index,
	item,
	control,
	selectableUserClassifications,
	editable,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"tagRelativeToTag">> & {
	selectableUserClassifications: UserInstrumentClassificationDto[] | undefined;
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"tagRelativeToTag">;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const { setValue } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};
	const availableTagClassifications = useMemo(
		() => selectableUserClassifications?.filter((x) => x.fieldType === "TAG") ?? [],
		[selectableUserClassifications],
	);
	return (
		<>
			{(
				[
					{
						optionsField: "options",
						uuidField: "classificationUuid",
						title: "Tag",
						qualifier: "tag",
					},
					{
						optionsField: "relativeToOptions",
						uuidField: "relativeToClassificationUuid",
						title: "Relative to",
						qualifier: "relativeToTag",
					},
				] as const
			).map(({ optionsField, uuidField, title, qualifier }) => {
				// The radio picker yields a single option, so we read/write the first element of the array.
				const currentValue = item[optionsField][0] ?? null;
				const classification = availableTagClassifications.find((x) => x.classificationUuid === item[uuidField]);
				const tagColor = classificationOptionColor(classification, currentValue);
				const otherOptionsField = optionsField === "options" ? "relativeToOptions" : "options";
				const otherUuidField =
					uuidField === "classificationUuid" ? "relativeToClassificationUuid" : "classificationUuid";
				const otherValue = item[otherOptionsField][0] ?? null;
				const otherClassification = availableTagClassifications.find(
					(x) => x.classificationUuid === item[otherUuidField],
				);
				return (
					<div key={optionsField} className="max-w-[180px] min-[1820px]:max-w-[300px] overflow-hidden pr-4">
						<div className={titleClassName}>{title}</div>
						{item.readonly || !editable ? (
							currentValue ? (
								<TagBadge label={currentValue} color={tagColor} />
							) : (
								<EllipsisText text="-" />
							)
						) : (
							<FormController
								control={control}
								name={`list.${index}.${optionsField}`}
								render={({ fieldState }) => {
									const error = Boolean(fieldState.error?.message);
									const invalid = isInvalid({
										error,
										validity: item.validity,
										mode: solvedWithAI ? "ai" : "edit",
									});
									return (
										<AutoTooltip
											severity="error"
											position="left"
											mode="hover"
											disabled={!error}
											trigger={({ innerRef }) => (
												<ActionText
													data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/${qualifier}`}
													innerRef={innerRef}
													disabled={availableTagClassifications.length === 0}
													onClick={() =>
														spawnClassificationDialog({
															mode: "radio",
															selectableUserClassifications: availableTagClassifications,
															initialValue: item[uuidField]
																? {
																		classificationUuid: item[uuidField],
																		selectedOptions: item[optionsField],
																	}
																: undefined,
															disabledRowIds:
																otherValue && otherClassification?.classificationUuid
																	? [userClassificationId(otherClassification.classificationUuid, otherValue)]
																	: [],
															optionsProvider(selectedClassificationUuid) {
																return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																	ctx.area,
																	{
																		classificationUuid: selectedClassificationUuid,
																		universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																	},
																);
															},
															onSubmit({ classification, selectedOptions }) {
																if (selectedOptions.length === 0) {
																	return;
																}
																setValue(`list.${index}.${optionsField}`, selectedOptions);
																setValue(`list.${index}.${uuidField}`, classification.classificationUuid ?? null);
																trigger(`list.${index}.${optionsField}`);
																onConstraintChange();
															},
														})
													}
													className={{
														"w-full": true,
														[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
													}}
												>
													{currentValue ? (
														<TagBadge label={currentValue} color={tagColor} />
													) : (
														<EllipsisText text="Select a value" className="text-left" />
													)}
												</ActionText>
											)}
										>
											{fieldState.error?.message}
										</AutoTooltip>
									);
								}}
							/>
						)}
					</div>
				);
			})}
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
				overrideRelationOptions={excludeRelationOption(["RELATIVE_TO_REFERENCE", "EXCLUDE"])}
			/>
		</>
	);
}
//#endregion

//#region Currency Constraints
function CurrencyColumn({
	index,
	item,
	control,
	editable,
	selectableCurrencies,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
	selectableUserClassifications,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"currencyConstraints">> & {
	editable: boolean;
	trigger(fieldName: string): void;
	selectableCurrencies: SelectableAllocationConstraintsResponse["selectableCurrencies"];
	selectableUserClassifications?: UserInstrumentClassificationDto[];
	solvedWithAI: boolean;
	revertData?: EditablePortfolioConstraintHelper<"currencyConstraints">;
	onConstraintChange(): void;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const { setValue, getValues } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};

	const { currencies, ...common } = item;
	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div
					className={toClassName({
						[titleClassName]: true,
					})}
				>
					{currencies.at(0) ? "Risk model" : "Currency"}
				</div>
				<div className="w-32 text-left">
					{item.readonly || !editable ? (
						(item.currencies?.[0] ?? "-")
					) : (
						<FormController
							control={control}
							name={`list.${index}.currencies`}
							render={({ field: { ref: _fieldRef, ...fieldProps }, fieldState }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});

								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!invalid}
										overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/customCurrencyConstraint`}
												innerRef={innerRef}
												disabled={!selectableUserClassifications || !selectableCurrencies}
												onClick={() =>
													selectableUserClassifications &&
													selectableCurrencies &&
													spawnCurrencyConstraintDialog({
														classificationType: "RISK_MODEL",
														initialValue: {
															selectedOptions: getValues(`list.${index}.currencies`),
														},
														optionsProvider(selectedClassificationUuid) {
															return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																ctx.area,
																{
																	classificationUuid: selectedClassificationUuid,
																	universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																},
															);
														},
														selectableUserClassifications,
														selectableCurrencies,
														disabledRowIds: getValues().list.flatMap((constraints, i) =>
															i === index // self can always be changed
																? []
																: constraints.type === "currencyConstraints"
																	? constraints.currencies.map((currency) => currency)
																	: constraints.type === "customCurrencyConstraint"
																		? constraints.options.map((value) =>
																				userClassificationId(constraints.classificationUuid!, value),
																			)
																		: [],
														),
														onSubmit(data) {
															if (data.type === "RISK_MODEL") {
																fieldProps.onChange(data.selectedOptions);
																trigger(`list.${index}.currencies`);
																onConstraintChange();
																return;
															}

															if (data.type === "TAG") {
																setValue(`list.${index}`, {
																	...common,
																	tagType: "CURRENCY",
																	type: "customCurrencyConstraint",
																	applicationMode: "PORTFOLIO_EXPOSURE",
																	options: data.selectedOptions,
																	classificationUuid: data.classification!.classificationUuid!,
																	standardClassification: data.classification?.standardClassificationId ?? null,
																} satisfies EditablePortfolioConstraintHelper<"customCurrencyConstraint">);
																trigger(`list.${index}.currencies`);
																onConstraintChange();
															}
														},
													})
												}
												className={{
													"w-full": true,
													[`!text-[color:var(--palette-P400)]`]: fieldProps.value.length > 0 && !invalid,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												<EllipsisText text={fieldProps.value?.[0] ?? "Select value"} className="text-left" />
											</ActionText>
										)}
									>
										{fieldState.error?.message ?? (
											<TooltipContent className="font-bold mb-2">Constraint not feasible</TooltipContent>
										)}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}
//#endregion

//#region Max weight on single instrument
function InstrumentMaxWeightColumn({
	index,
	item,
	control,
	editable,
	solvedWithAI,
	revertData,
	onConstraintChange,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"instrumentMaxWeightConstraint">> & {
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"instrumentMaxWeightConstraint">;
}) {
	const { formatNumber } = useLocaleFormatters();
	const originalValues = revertData && {
		relation: ConstraintRelation.Max,
		maxWeight: revertData.weight ?? null,
		minWeight: null,
	};
	return (
		<div className="grow grid justify-end mr-4">
			<div
				className={toClassName({
					[titleClassName]: true,
					"text-right": true,
				})}
			>
				Weight
			</div>
			<div className="w-20 text-right">
				{item.readonly || !editable ? (
					`${formatNumber(item.weight, 2)}%`
				) : (
					<FormController
						control={control}
						name={`list.${index}.weight`}
						render={({ field: { ref: fieldRef, onBlur, ...fieldProps }, fieldState }) => {
							const error = Boolean(fieldState.error?.message);
							const invalid = isInvalid({
								error,
								validity: item.validity,
								mode: solvedWithAI ? "ai" : "edit",
							});

							return (
								<AutoTooltip
									severity="error"
									position="left"
									mode="hover"
									disabled={solvedWithAI ? undefined : !invalid}
									overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
									trigger={({ innerRef }) => (
										<NumberInput
											inputAppearance={{
												style: solvedWithAI ? { borderColor: themeCSSVars.palette_graph_B500 } : undefined,
											}}
											data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/weight`}
											{...fieldProps}
											onBlur={(e) => {
												onBlur();
												if (!equal(String(e.currentTarget.value), String(fieldProps.value))) {
													onConstraintChange();
												}
											}}
											innerRef={multiRef(innerRef, fieldRef)}
											rightContent={<Icon icon="Percentile" />}
											size="x-small"
											placeholder="100"
											invalid={invalid}
										/>
									)}
								>
									<ConstraintTooltipContent
										mode={solvedWithAI ? "ai" : "edit"}
										originalValues={originalValues}
										fieldState={fieldState}
										item={{
											relation: ConstraintRelation.Max,
											suggestedMaxWeight: item.suggestedMaxWeight,
											suggestedMinWeight: item.suggestedMinWeight,
										}}
									/>
								</AutoTooltip>
							);
						}}
					/>
				)}
			</div>
		</div>
	);
}
//#endregion

//#region Lock instruments Constraint
function LockInstrumentGroupColumn({
	control,
	index,
	item,
	selectableLockableInstruments,
	editable,
	solvedWithAI,
	onConstraintChange,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"lockInstrumentGroup">> & {
	selectableLockableInstruments: SelectableAllocationConstraintsResponse["selectableLockableInstruments"];
	editable: boolean;
	solvedWithAI: boolean;
	onConstraintChange(): void;
}) {
	const { t } = useTranslation();

	return (
		<div className="flex-1">
			<div className="max-w-[180px] min-[1820px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>Instruments</div>
				{item.readonly || !editable ? (
					<EllipsisText text={instrumentGroupToLabel(item.tickerIdentifiers)} />
				) : (
					<FormController
						control={control}
						name={`list.${index}.tickerIdentifiers`}
						render={({ field: { onChange: onSubmit, ref: fieldRef, value: selected }, fieldState }) => {
							const error = Boolean(fieldState.error?.message);
							const invalid = isInvalid({
								error,
								validity: item.validity,
								mode: solvedWithAI ? "ai" : "edit",
							});
							function removeUnfeasibleInstruments(instruments: Array<ConstrainedTicker>) {
								onSubmit(instruments.filter((x) => x.feasible));
								onConstraintChange();
							}
							const unfeasibleConstraint = selected.filter((x) => x.feasible === false);
							const activeSelection = selected.filter((x) => !x.deleted);

							return (
								<div className="flex space-x-2 items-end">
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										overrideColor={!solvedWithAI || error ? undefined : themeCSSVars.palette_graph_B500}
										trigger={({ innerRef }) => (
											<ActionText
												disabled={!selectableLockableInstruments}
												onClick={() =>
													selectableLockableInstruments &&
													spawnInstrumentDialog({
														instruments: selectableLockableInstruments,
														rowClassName: `portfolioConstraintsTable/constraint(${item.type},${index})/row`,
														selected: activeSelection,
														onSubmit: (instruments) => {
															const deletedInstruments = selected.filter((x) => x.deleted);
															const joinedInstruments = [...deletedInstruments, ...instruments];
															const listWithUniqueInstruments = joinedInstruments.reduce((acc, instrument) => {
																if (!acc.has(instrument.ticker)) {
																	return acc.set(instrument.ticker, instrument);
																}

																return acc.set(instrument.ticker, {
																	...instrument,
																	deleted: false,
																});
															}, Map());
															onSubmit(Array.from(listWithUniqueInstruments.values()));
															onConstraintChange();
														},
													})
												}
												innerRef={multiRef(innerRef, fieldRef)}
												className={{
													"truncate pt-[2px] pb-[4px] leading-none!": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												{instrumentGroupToLabel(activeSelection)}
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
									{item.validity === ConstraintValidity.Invalid &&
										activeSelection.some((x) => x.feasible === false) &&
										!solvedWithAI && (
											<AutoTooltip
												severity="warning"
												position="right"
												mode="hover"
												overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
												trigger={({ innerRef }) => (
													<Icon
														icon={solvedWithAI ? "Ask-ai" : "Icon-full-alert"}
														color={solvedWithAI ? themeCSSVars.palette_graph_B500 : themeCSSVars.palette_W500}
														innerRef={innerRef}
														size={16}
													/>
												)}
											>
												<TooltipContent>
													<div className="flex space-x-1 mb-2">
														<Icon
															icon={solvedWithAI ? "Ask-ai" : "Icon-full-alert"}
															color={solvedWithAI ? themeCSSVars.palette_graph_B500 : themeCSSVars.palette_W500}
															size={16}
														/>
														<Text as="span" type="Body/M/Bold">
															Unfeasible constraints
														</Text>
													</div>
													{unfeasibleConstraint.length === 0 ? (
														<>
															<Text as="div" type="Body/M/Book" className="mb-2">
																Remove the constraint or change the constraints order.
															</Text>
															<Text as="div" type="Body/M/Book" className="mb-2 italic">
																Current instrument weight: {unfeasibleConstraint[0]?.weight}%
															</Text>
														</>
													) : (
														<>
															<Text as="div" type="Body/M/Book" className="mb-2">
																One of the values in the group is not feasible.
															</Text>
															<ul className="list-disc pl-4 mb-4">
																<ForEach collection={selected.filter((x) => x.feasible === false)}>
																	{(subItem) => <li>{subItem.instrument}</li>}
																</ForEach>
															</ul>
															<Text as="p" type="Body/M/Book">
																You can solve it by removing the lock constraint on the highlighted instruments.{" "}
																<ActionText
																	className="inline-block"
																	onClick={() => removeUnfeasibleInstruments(activeSelection)}
																>
																	{t("BUTTON.REMOVE")}
																</ActionText>
															</Text>{" "}
														</>
													)}
												</TooltipContent>
											</AutoTooltip>
										)}
								</div>
							);
						}}
					/>
				)}
			</div>
		</div>
	);
}
//#endregion

//#region For each instruments Constraint
function ForEachConstraintColumn({
	index,
	item,
	selectableInstruments,
	editable,
	control,
	solvedWithAI,
	onConstraintChange,
	trigger,
	revertData,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"forEachConstraint">> & {
	selectableInstruments: SelectableAllocationConstraintsResponse["selectableLockableInstruments"];
	editable: boolean;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	trigger(fieldName: string): void;
	revertData?: EditablePortfolioConstraintHelper<"forEachConstraint">;
}) {
	const { t } = useTranslation();
	const { formatNumber } = useLocaleFormatters();
	const { resetField } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();

	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};

	const showSuggestionTooltip =
		revertData?.tickerIdentifiers !== undefined && revertData.tickerIdentifiers.length === 1;

	return (
		<div className="flex-1 flex">
			<div className="max-w-[180px] min-[1820px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>Instruments</div>
				{item.readonly || !editable ? (
					<EllipsisText text={instrumentGroupToLabel(item.tickerIdentifiers)} />
				) : (
					<FormController
						control={control}
						name={`list.${index}.tickerIdentifiers`}
						render={({ field: { onChange: onSubmit, ref: fieldRef, value: selected }, fieldState }) => {
							const error = Boolean(fieldState.error?.message);
							function removeUnfeasibleInstruments(instruments: Array<ConstrainedTicker>) {
								onSubmit(instruments.filter((x) => x.feasible));
								trigger(`list.${index}.tickerIdentifiers`);
								onConstraintChange();
							}

							const activeSelection = selected.filter((x) => !x.deleted);

							return (
								<div className="flex space-x-2 items-start">
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										overrideColor={!solvedWithAI || error ? undefined : themeCSSVars.palette_graph_B500}
										trigger={({ innerRef }) => (
											<ActionText
												disabled={!selectableInstruments}
												onClick={() =>
													selectableInstruments &&
													spawnInstrumentDialog({
														instruments: selectableInstruments,
														rowClassName: `portfolioConstraintsTable/constraint(${item.type},${index})/row`,
														selected: activeSelection,
														onSubmit: (instruments) => {
															const deletedInstruments = selected.filter((x) => x.deleted);
															const joinedInstruments = [...deletedInstruments, ...instruments];
															const listWithUniqueInstruments = joinedInstruments.reduce((acc, instrument) => {
																if (!acc.has(instrument.ticker)) {
																	return acc.set(instrument.ticker, instrument);
																}

																return acc.set(instrument.ticker, {
																	...instrument,
																	deleted: false,
																});
															}, Map());
															onSubmit(Array.from(listWithUniqueInstruments.values()));
															onConstraintChange();
														},
													})
												}
												innerRef={multiRef(innerRef, fieldRef)}
												className={{
													"truncate pt-[2px] pb-[4px] leading-none!": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: error,
												}}
											>
												{instrumentGroupToLabel(activeSelection)}
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
									{item.validity === ConstraintValidity.Invalid &&
										activeSelection.some((x) => x.feasible === false) &&
										!solvedWithAI && (
											<AutoTooltip
												severity="warning"
												position="right"
												mode="hover"
												trigger={({ innerRef }) => (
													<Icon
														icon="Icon-full-alert"
														color={themeCSSVars.palette_W500}
														innerRef={innerRef}
														size={16}
													/>
												)}
											>
												<TooltipContent>
													<div className="flex space-x-1 mb-2">
														<Icon icon="Icon-full-alert" color={themeCSSVars.palette_W500} size={16} />
														<Text as="span" type="Body/M/Bold">
															Unfeasible constraints
														</Text>
													</div>
													<Text as="div" type="Body/M/Book" className="mb-2">
														One of the values in the group is not feasible.
													</Text>
													<ul className="list-disc pl-4 mb-4">
														<ForEach collection={activeSelection}>{({ instrument }) => <li>{instrument}</li>}</ForEach>
													</ul>
													<Text as="p" type="Body/M/Book">
														You can solve it by removing the lock constraint on the highlighted instruments.{" "}
														<ActionText
															className="inline-block"
															onClick={() => removeUnfeasibleInstruments(activeSelection)}
														>
															{t("BUTTON.REMOVE")}
														</ActionText>
													</Text>
												</TooltipContent>
											</AutoTooltip>
										)}
								</div>
							);
						}}
					/>
				)}
			</div>
			<div className="flex-1 flex space-x-4 mr-4 justify-end items-start">
				{item.readonly || !editable ? (
					<ReadonlyRelation label="OPERATOR" className="w-28">
						{t(`RELATION.${item.relation}`)}
					</ReadonlyRelation>
				) : (
					<FormController
						control={control}
						name={`list.${index}.relation`}
						render={({ field: { ref: fieldRef, ...fieldProps } }) => (
							<ReadonlyRelation label="OPERATOR" className="w-28">
								<Select
									data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/relation`}
									{...fieldProps}
									innerRef={fieldRef}
									options={excludeRelationOption(["RELATIVE_TO_REFERENCE"])}
									size="x-small"
									onChange={(constraint) => {
										fieldProps.onChange(constraint);
										resetField(`list.${index}.maxWeight`, {
											defaultValue: null,
										});
										resetField(`list.${index}.minWeight`, {
											defaultValue: null,
										});
										onConstraintChange();
									}}
									className="w-full"
								/>
							</ReadonlyRelation>
						)}
					/>
				)}
				<div
					className={toClassName({
						"min-w-[196px] flex": true,
					})}
				>
					{match(item)
						.with({ relation: "BETWEEN" }, (constraint) => (
							<>
								{constraint.readonly || !editable ? (
									<>
										<ReadonlyRelation className="w-20 ml-auto text-right" label="MIN" align="right">
											{formatNumber(constraint.minWeight, 2)}%
										</ReadonlyRelation>
										<ReadonlyRelation className="w-20 ml-auto text-right" label="MAX" align="right">
											{formatNumber(constraint.maxWeight, 2)}%
										</ReadonlyRelation>
									</>
								) : (
									<>
										<FormController
											control={control}
											name={`list.${index}.minWeight`}
											render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => {
												const error = Boolean(fieldState.error?.message);
												const invalid = isInvalid({
													error,
													validity: item.validity,
													mode: solvedWithAI ? "ai" : "edit",
												});

												return (
													<AutoTooltip
														severity="error"
														position="left"
														mode="hover"
														disabled={solvedWithAI && showSuggestionTooltip ? undefined : !invalid}
														overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
														trigger={({ innerRef }) => (
															<ReadonlyRelation className="w-20 ml-auto" label="MIN" align="right">
																<NumberInput
																	data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/minWeight`}
																	inputAppearance={solveWithAiInputStyle(solvedWithAI && showSuggestionTooltip)}
																	innerRef={multiRef(ref, innerRef)}
																	{...fieldProps}
																	invalid={error}
																	onBlur={(e) => {
																		onBlur();
																		trigger(`list.${index}.maxWeight`);
																		if (!equal(String(e.currentTarget.value), String(fieldProps.value))) {
																			onConstraintChange();
																		}
																	}}
																	size="x-small"
																	rightContent={<Icon icon="Percentile" />}
																	placeholder="0"
																/>
															</ReadonlyRelation>
														)}
													>
														<ConstraintTooltipContent
															mode={solvedWithAI ? "ai" : "edit"}
															originalValues={originalValues}
															fieldState={fieldState}
															item={item}
														/>
													</AutoTooltip>
												);
											}}
										/>

										<FormController
											control={control}
											name={`list.${index}.maxWeight`}
											render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => {
												const error = Boolean(fieldState.error?.message);
												const invalid = isInvalid({
													error,
													validity: item.validity,
													mode: solvedWithAI ? "ai" : "edit",
												});

												return (
													<AutoTooltip
														severity="error"
														position="left"
														mode="hover"
														disabled={solvedWithAI && showSuggestionTooltip ? undefined : !invalid}
														overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
														trigger={({ innerRef }) => (
															<ReadonlyRelation className="w-20 ml-auto" label="MAX" align="right">
																<NumberInput
																	data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/maxWeight`}
																	inputAppearance={solveWithAiInputStyle(solvedWithAI && showSuggestionTooltip)}
																	innerRef={multiRef(ref, innerRef)}
																	{...fieldProps}
																	invalid={error}
																	onBlur={(e) => {
																		onBlur();
																		trigger(`list.${index}.minWeight`);
																		if (!equal(String(e.currentTarget.value), String(fieldProps.value))) {
																			onConstraintChange();
																		}
																	}}
																	size="x-small"
																	rightContent={<Icon icon="Percentile" />}
																	placeholder="100"
																/>
															</ReadonlyRelation>
														)}
													>
														<ConstraintTooltipContent
															mode={solvedWithAI ? "ai" : "edit"}
															originalValues={originalValues}
															fieldState={fieldState}
															item={item}
														/>
													</AutoTooltip>
												);
											}}
										/>
									</>
								)}
							</>
						))
						.with({ relation: "MAX" }, (constraint) =>
							constraint.readonly || !editable ? (
								<ReadonlyRelation className="w-20 ml-auto text-right" label="MAX" align="right">
									{constraint.maxWeight}%
								</ReadonlyRelation>
							) : (
								<FormController
									control={control}
									name={`list.${index}.maxWeight`}
									render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => {
										const error = Boolean(fieldState.error?.message);
										const invalid = isInvalid({
											error,
											validity: item.validity,
											mode: solvedWithAI ? "ai" : "edit",
										});

										return (
											<AutoTooltip
												severity="error"
												position="left"
												mode="hover"
												disabled={solvedWithAI && showSuggestionTooltip ? undefined : !invalid}
												overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
												trigger={({ innerRef }) => (
													<ReadonlyRelation className="w-20 ml-auto" label="MAX" align="right">
														<NumberInput
															data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/maxWeight`}
															inputAppearance={solveWithAiInputStyle(solvedWithAI && showSuggestionTooltip)}
															innerRef={multiRef(ref, innerRef)}
															{...fieldProps}
															invalid={error}
															onBlur={(e) => {
																onBlur();
																if (!equal(String(e.currentTarget.value), String(fieldProps.value))) {
																	onConstraintChange();
																}
															}}
															size="x-small"
															rightContent={<Icon icon="Percentile" />}
															placeholder="100"
														/>
													</ReadonlyRelation>
												)}
											>
												<ConstraintTooltipContent
													mode={solvedWithAI ? "ai" : "edit"}
													originalValues={originalValues}
													fieldState={fieldState}
													item={item}
												/>
											</AutoTooltip>
										);
									}}
								/>
							),
						)
						.with({ relation: "MIN" }, (constraint) =>
							constraint.readonly || !editable ? (
								<ReadonlyRelation className="w-20 ml-auto text-right" label="MIN" align="right">
									{constraint.minWeight}%
								</ReadonlyRelation>
							) : (
								<FormController
									control={control}
									name={`list.${index}.minWeight`}
									render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => {
										const error = Boolean(fieldState.error?.message);
										const invalid = isInvalid({
											error,
											validity: item.validity,
											mode: solvedWithAI ? "ai" : "edit",
										});

										return (
											<AutoTooltip
												severity="error"
												position="left"
												mode="hover"
												disabled={solvedWithAI && showSuggestionTooltip ? undefined : !invalid}
												overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
												trigger={({ innerRef }) => (
													<ReadonlyRelation className="w-20 ml-auto" label="MIN" align="right">
														<NumberInput
															data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/minWeight`}
															inputAppearance={solveWithAiInputStyle(solvedWithAI && showSuggestionTooltip)}
															innerRef={multiRef(ref, innerRef)}
															{...fieldProps}
															invalid={error}
															onBlur={(e) => {
																onBlur();
																trigger(`list.${index}.maxWeight`);
																if (!equal(String(e.currentTarget.value), String(fieldProps.value))) {
																	onConstraintChange();
																}
															}}
															size="x-small"
															rightContent={<Icon icon="Percentile" />}
															placeholder="0"
														/>
													</ReadonlyRelation>
												)}
											>
												<ConstraintTooltipContent
													mode={solvedWithAI ? "ai" : "edit"}
													originalValues={originalValues}
													fieldState={fieldState}
													item={item}
												/>
											</AutoTooltip>
										);
									}}
								/>
							),
						)
						.with({ relation: "EQUAL" }, (constraint) =>
							constraint.readonly || !editable ? (
								<ReadonlyRelation className="w-20 ml-auto text-right" label="WEIGHT" align="right">
									{constraint.minWeight}%
								</ReadonlyRelation>
							) : (
								<FormController
									control={control}
									name={`list.${index}.minWeight`}
									render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => {
										const error = Boolean(fieldState.error?.message);
										const invalid = isInvalid({
											error,
											validity: item.validity,
											mode: solvedWithAI ? "ai" : "edit",
										});

										return (
											<AutoTooltip
												severity="error"
												position="left"
												mode="hover"
												disabled={solvedWithAI && showSuggestionTooltip ? undefined : !invalid}
												overrideColor={!solvedWithAI ? undefined : themeCSSVars.palette_graph_B500}
												trigger={({ innerRef }) => (
													<ReadonlyRelation className="w-20 ml-auto" align="right">
														<NumberInput
															data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/minWeight`}
															inputAppearance={solveWithAiInputStyle(solvedWithAI && showSuggestionTooltip)}
															innerRef={multiRef(ref, innerRef)}
															{...fieldProps}
															invalid={error}
															onBlur={(e) => {
																onBlur();
																if (!equal(String(e.currentTarget.value), String(fieldProps.value))) {
																	onConstraintChange();
																}
															}}
															size="x-small"
															rightContent={<Icon icon="Percentile" />}
															placeholder="0"
														/>
													</ReadonlyRelation>
												)}
											>
												<ConstraintTooltipContent
													mode={solvedWithAI ? "ai" : "edit"}
													originalValues={originalValues}
													fieldState={fieldState}
													item={item}
												/>
											</AutoTooltip>
										);
									}}
								/>
							),
						)
						.otherwise(() => (
							<></>
						))}
				</div>
			</div>
		</div>
	);
}
//#endregion

//#region Custom Tag Constraint(Currencies);
function CustomCurrencyConstraint({
	index,
	item,
	control,
	editable,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
	selectableUserClassifications,
	selectableCurrencies,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"customCurrencyConstraint">> & {
	selectableUserClassifications?: UserInstrumentClassificationDto[];
	selectableCurrencies: SelectableAllocationConstraintsResponse["selectableCurrencies"];
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"customCurrencyConstraint">;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};
	const { setValue, getValues } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();

	const {
		applicationMode,
		tagType: _tagType,
		standardClassification: _standardClassification,
		classificationUuid,
		options,
		...common
	} = item;
	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>{item.options?.[0] ? "Currency Tag" : "Currency"}</div>
				<div>
					{item.readonly || !editable ? (
						item.options?.[0] ? (
							<TagBadge
								label={item.options[0]}
								color={classificationOptionColor(
									selectableUserClassifications?.find((c) => c.classificationUuid === item.classificationUuid),
									item.options[0],
								)}
							/>
						) : (
							<EllipsisText text="-" />
						)
					) : (
						<FormController
							control={control}
							name={`list.${index}.options`}
							render={({ fieldState, field }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});
								const selectedClassification = selectableUserClassifications?.find(
									(c) => c.classificationUuid === item.classificationUuid,
								);
								const selectedValue = item.options?.[0];
								const tagColor = classificationOptionColor(selectedClassification, selectedValue);

								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/customCurrencyConstraint`}
												innerRef={innerRef}
												disabled={!selectableUserClassifications || !selectableCurrencies}
												onClick={() =>
													selectableUserClassifications &&
													selectableCurrencies &&
													spawnCurrencyConstraintDialog({
														classificationType: "TAG",
														initialValue: classificationUuid
															? {
																	classificationUuid: classificationUuid ?? "",
																	selectedOptions: options,
																}
															: undefined,
														optionsProvider(selectedClassificationUuid) {
															return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																ctx.area,
																{
																	classificationUuid: selectedClassificationUuid,
																	universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																},
															);
														},
														selectableUserClassifications,
														selectableCurrencies,
														disabledRowIds: getValues().list.flatMap((constraints, i) =>
															i === index // self can always be changed
																? []
																: constraints.type === "currencyConstraints"
																	? constraints.currencies.map((currency) => currency)
																	: constraints.type === "customCurrencyConstraint"
																		? constraints.options.map((value) =>
																				userClassificationId(constraints.classificationUuid!, value),
																			)
																		: [],
														),
														onSubmit(data) {
															if (data.type === "RISK_MODEL") {
																setValue(`list.${index}`, {
																	...common,
																	type: "currencyConstraints",
																	currencies: data.selectedOptions,
																} satisfies EditablePortfolioConstraintHelper<"currencyConstraints">);
																trigger(`list.${index}.currencies`);
																return;
															}

															if (data.type === "TAG") {
																setValue(`list.${index}`, {
																	...common,
																	type: "customCurrencyConstraint",
																	applicationMode,
																	options: data.selectedOptions,
																	classificationUuid: data.classification!.classificationUuid!,
																	standardClassification: data.classification!.standardClassificationId ?? null,
																	tagType: "CURRENCY",
																} satisfies EditablePortfolioConstraintHelper<"customCurrencyConstraint">);
																trigger(`list.${index}.options`);
															}
														},
													})
												}
												className={{
													"w-full": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												{selectedValue ? (
													<TagBadge label={selectedValue} color={tagColor} />
												) : (
													<EllipsisText text="Select value" className="text-left" />
												)}
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}
//#endregion

//#region Custom Tag Constraint(Asset Class);
function CustomAssetClassConstraint({
	index,
	item,
	control,
	editable,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
	selectableUserClassifications,
	selectableAssetClasses,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"customAssetClassConstraint">> & {
	selectableUserClassifications?: UserInstrumentClassificationDto[];
	selectableAssetClasses: SelectableAllocationConstraintsResponse["selectableAssetClasses"];
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"customAssetClassConstraint">;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};
	const { setValue, getValues } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();
	const {
		applicationMode,
		tagType: _tagType,
		standardClassification: _standardClassification,
		classificationUuid,
		options,
		...common
	} = item;
	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>{item.options?.[0] ? "Asset Class Tag" : "Asset Class"}</div>
				<div>
					{item.readonly || !editable ? (
						item.options?.[0] ? (
							<TagBadge
								label={item.options[0]}
								color={classificationOptionColor(
									selectableUserClassifications?.find((c) => c.classificationUuid === item.classificationUuid),
									item.options[0],
								)}
							/>
						) : (
							<EllipsisText text="-" />
						)
					) : (
						<FormController
							control={control}
							name={`list.${index}.options`}
							render={({ fieldState, field }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});
								const selectedClassification = selectableUserClassifications?.find(
									(c) => c.classificationUuid === item.classificationUuid,
								);
								const selectedValue = item.options?.[0];
								const tagColor = classificationOptionColor(selectedClassification, selectedValue);

								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/customCurrencyConstraint`}
												innerRef={innerRef}
												disabled={!selectableAssetClasses || !selectableUserClassifications}
												onClick={() =>
													selectableAssetClasses &&
													selectableUserClassifications &&
													spawnAssetAllocationDialog({
														classificationType: "TAG",
														initialValue: classificationUuid
															? {
																	classificationUuid: classificationUuid ?? "",
																	selectedOptions: options,
																}
															: undefined,
														optionsProvider(selectedClassificationUuid) {
															return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																ctx.area,
																{
																	classificationUuid: selectedClassificationUuid,
																	universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																},
															);
														},
														disabledRowIds: getValues().list.flatMap((constraints, i) =>
															i === index // self can always be changed
																? []
																: constraints.type === "assetClassGroupConstraints"
																	? constraints.assetClassesIdentifiers.map((assetClass) =>
																			generateAssetClassId(assetClass),
																		)
																	: constraints.type === "customAssetClassConstraint"
																		? constraints.options.map((value) =>
																				userClassificationId(constraints.classificationUuid!, value),
																			)
																		: [],
														),
														selectableAssetClasses,
														selectableUserClassifications,
														onSubmit(data) {
															if (data.type === "RISK_MODEL") {
																setValue(`list.${index}`, {
																	...common,
																	type: "assetClassConstraints",
																	assetClassesIdentifiers: data.selectedOptions,
																} satisfies EditablePortfolioConstraintHelper<"assetClassConstraints">);
																trigger(`list.${index}.assetClassesIdentifiers`);
																onConstraintChange();
																return;
															}

															if (data.type === "TAG") {
																setValue(`list.${index}`, {
																	...common,
																	type: "customAssetClassConstraint",
																	applicationMode,
																	options: data.selectedOptions,
																	classificationUuid: data.instrumentClassification!.classificationUuid!,
																	standardClassification:
																		data.instrumentClassification?.standardClassificationId ?? null,
																	tagType: "ASSET_CLASS",
																} satisfies EditablePortfolioConstraintHelper<"customAssetClassConstraint">);
																trigger(`list.${index}.options`);
																onConstraintChange();
															}
														},
													})
												}
												className={{
													"w-full": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												{selectedValue ? (
													<TagBadge label={selectedValue} color={tagColor} />
												) : (
													<EllipsisText text="Select value" className="text-left" />
												)}
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}
//#endregion

//#region Custom Tag Constraint(General);
function CustomTagConstraint({
	index,
	item,
	control,
	editable,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
	selectableUserClassifications,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"customTagConstraint">> & {
	selectableUserClassifications?: UserInstrumentClassificationDto[];
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"customTagConstraint">;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};

	const { setValue, getValues } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();
	const {
		applicationMode,
		tagType: _tagType,
		standardClassification: _standardClassification,
		classificationUuid,
		options,
		...common
	} = item;

	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>General tag</div>
				<div>
					{item.readonly || !editable ? (
						item.options?.[0] ? (
							<TagBadge
								label={item.options[0]}
								color={classificationOptionColor(
									selectableUserClassifications?.find((c) => c.classificationUuid === item.classificationUuid),
									item.options[0],
								)}
							/>
						) : (
							<EllipsisText text="-" />
						)
					) : (
						<FormController
							control={control}
							name={`list.${index}.options`}
							render={({ fieldState, field: _field }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});
								const selectedClassification = selectableUserClassifications?.find(
									(c) => c.classificationUuid === item.classificationUuid,
								);
								const selectedValue = item.options?.[0];
								const tagColor = classificationOptionColor(selectedClassification, selectedValue);

								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/customTagConstraint`}
												innerRef={innerRef}
												disabled={!selectableUserClassifications}
												onClick={() =>
													selectableUserClassifications &&
													spawnClassificationDialog({
														initialValue: classificationUuid
															? {
																	classificationUuid,
																	selectedOptions: options,
																}
															: undefined,
														optionsProvider(selectedClassificationUuid) {
															return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																ctx.area,
																{
																	classificationUuid: selectedClassificationUuid,
																	universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																},
															);
														},
														tagType: "GENERAL",
														mode: "radio",
														selectableUserClassifications,
														disabledRowIds: getValues().list.flatMap((constraints, i) =>
															i === index // self can always be changed
																? []
																: constraints.type === "customTagConstraint"
																	? constraints.options.map((value) =>
																			userClassificationId(constraints.classificationUuid!, value),
																		)
																	: [],
														),
														onSubmit(data) {
															setValue(`list.${index}`, {
																...common,
																type: "customTagConstraint",
																applicationMode,
																options: data.selectedOptions,
																classificationUuid: data.classification.classificationUuid!,
																standardClassification: data.classification.standardClassificationId ?? null,
																tagType: "GENERAL",
															} satisfies EditablePortfolioConstraintHelper<"customTagConstraint">);
															trigger(`list.${index}.options`);
															onConstraintChange();
														},
													})
												}
												className={{
													"w-full": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												{selectedValue ? (
													<TagBadge label={selectedValue} color={tagColor} />
												) : (
													<EllipsisText text="Select value" className="text-left" />
												)}
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}
//#endregion

//#region Custom Score Constraint;
function CustomScoreConstraint({
	index,
	item,
	control,
	editable,
	trigger,
	solvedWithAI,
	onConstraintChange,
	revertData,
	selectableUserClassifications,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"customScoreConstraint">> & {
	selectableUserClassifications?: UserInstrumentClassificationDto[];
	editable: boolean;
	trigger(fieldName: string): void;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"customScoreConstraint">;
}) {
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};

	const { setValue, watch } = useFormContext<{
		list: EditablePortfolioConstraint[];
	}>();
	const scoreClassifications = useMemo(
		() =>
			selectableUserClassifications?.flatMap((x) => {
				if (x.fieldType === "SCORE") {
					const { fieldType } = x;
					return [{ ...x, fieldType }];
				}
				return [];
			}),
		[selectableUserClassifications],
	);

	const selectedClassification = useMemo(
		() => scoreClassifications?.find((x) => x.classificationUuid === item.classificationUuid),
		[item.classificationUuid, scoreClassifications],
	);

	const constraintList = watch("list");
	const preSelectedClassifications = constraintList.flatMap((constraint) => {
		if (constraint.type === "customScoreConstraint" && constraint.classificationUuid) {
			return [constraint.classificationUuid];
		}
		return [];
	});

	const currentScoreState = useMemo((): ScoreConstraintSelectionDialogProps["currentScoreState"] => {
		if (!item.classificationUuid) {
			return undefined;
		}

		return {
			actionOnNullScore: item.considerNullAsZero ? "CONSIDER_NULL_AS_SCORE" : "EXCLUDE",
			classificationUuid: item.classificationUuid,
		};
	}, [item]);

	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>Score & Metrics</div>
				<div>
					{item.readonly || !editable ? (
						<EllipsisText
							key={selectedClassification?.name}
							text={
								selectedClassification?.name
									? `${selectedClassification?.name} - ${
											item.considerNullAsZero ? "Include with 0 as score" : "Exclude"
										}`
									: "..."
							}
						/>
					) : (
						<FormController
							control={control}
							name={`list.${index}.classificationUuid`}
							render={({ fieldState }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});

								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/customScoreConstraint`}
												innerRef={innerRef}
												disabled={!scoreClassifications}
												onClick={() =>
													scoreClassifications &&
													spawnScoreConstraintSelectionDialog({
														preSelectedClassifications,
														scoreClassifications,
														currentScoreState,
														onSubmit(selection) {
															setValue(
																`list.${index}`,
																{
																	...item,
																	applicationMode: "PORTFOLIO_EXPOSURE",
																	classificationUuid: selection.classificationUuid,
																	standardClassification: selection.standardClassification ?? null,
																	considerNullAsZero: selection.actionOnNullScore === "CONSIDER_NULL_AS_SCORE",
																} satisfies EditablePortfolioConstraintHelper<"customScoreConstraint">,
																{ shouldDirty: true },
															);
															trigger(`list.${index}.classificationUuid`);
															trigger(`list.${index}.actionOnNullScore`);
														},
													})
												}
												className={{
													"w-full": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												<EllipsisText
													text={
														selectedClassification?.name
															? `${selectedClassification?.name} - ${
																	item.considerNullAsZero ? "Include with 0 as score" : "Exclude"
																}`
															: "Select value"
													}
													className="text-left"
												/>
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				type="number"
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}
//#endregion

//#region Group AssetClass Constraints
function AssetAllocationColumnsGroup({
	index,
	item,
	control,
	selectableUserClassifications,
	selectableAssetClasses,
	editable,
	solvedWithAI,
	onConstraintChange,
	revertData,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"assetClassGroupConstraints">> & {
	selectableUserClassifications: UserInstrumentClassificationDto[] | undefined;
	selectableAssetClasses: SelectableAllocationConstraintsResponse["selectableAssetClasses"] | undefined;
	editable: boolean;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"assetClassGroupConstraints">;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};

	const { setValue, getValues, trigger } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();
	const { assetClassesIdentifiers, ...common } = item;
	const assetClassGroupLabel = useMemo(() => {
		if (assetClassesIdentifiers.length === 1) {
			return assetClassesIdentifiers.at(0)?.name;
		}

		if (assetClassesIdentifiers.length > 1) {
			return `Selected (${assetClassesIdentifiers.length} asset allocations)`;
		}

		return "Select values";
	}, [assetClassesIdentifiers]);

	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>{item.assetClassesIdentifiers.at(0)?.name ? "Risk model" : "Asset class"}</div>
				<div>
					{item.readonly || !editable ? (
						<EllipsisText text={assetClassGroupLabel} />
					) : (
						<FormController
							control={control}
							name={`list.${index}.assetClassesIdentifiers`}
							render={({ fieldState, field }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});
								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/assetClassesIdentifiers`}
												innerRef={innerRef}
												disabled={!selectableAssetClasses || !selectableUserClassifications}
												onClick={() =>
													selectableAssetClasses &&
													selectableUserClassifications &&
													spawnAssetAllocationDialog({
														classificationType: "RISK_MODEL",
														initialValue: {
															selectedOptions: getValues(`list.${index}.assetClassesIdentifiers`),
														},
														mode: "checkbox",
														optionsProvider(selectedClassificationUuid) {
															return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																ctx.area,
																{
																	classificationUuid: selectedClassificationUuid,
																	universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																},
															);
														},
														// disabledRowIds: getValues().list.flatMap((constraints, i) =>
														// 	i === index // self can always be changed
														// 		? []
														// 		: constraints.type === "assetClassGroupConstraints"
														// 		  ? constraints.assetClassesIdentifiers.map((assetClass) =>
														// 					generateAssetClassId(assetClass),
														// 		    )
														// 		  : constraints.type === "customGroupAssetClassConstraints"
														// 		    ? constraints.options.map((value) =>
														// 						userClassificationId(constraints.classificationUuid!, value),
														// 		      )
														// 		    : [],
														// ),
														selectableAssetClasses,
														selectableUserClassifications,
														onSubmit(data) {
															if (data.type === "RISK_MODEL") {
																setValue(`list.${index}.assetClassesIdentifiers`, data.selectedOptions);
																trigger(`list.${index}.assetClassesIdentifiers`).catch(noop);
																onConstraintChange();
																return;
															}

															if (data.type === "TAG") {
																setValue(`list.${index}`, {
																	...common,
																	type: "customGroupAssetClassConstraints",
																	applicationMode: "PORTFOLIO_EXPOSURE",
																	options: data.selectedOptions,
																	classificationUuid: data.instrumentClassification?.classificationUuid ?? null,
																	tagType: "ASSET_CLASS",
																	standardClassification:
																		data.instrumentClassification?.standardClassificationId ?? null,
																	suggestedMaxWeight: null,
																	suggestedMinWeight: null,
																} satisfies EditablePortfolioConstraintHelper<"customGroupAssetClassConstraints">);
																trigger(`list.${index}.standardClassification`).catch(noop);
																onConstraintChange();
															}
														},
													})
												}
												className={{
													"w-full": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												<EllipsisText text={assetClassGroupLabel} />
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}

//#region Custom Group AssetClass Constraints
function CustomGroupAssetAllocationColumns({
	index,
	item,
	control,
	selectableUserClassifications,
	selectableAssetClasses,
	editable,
	solvedWithAI,
	onConstraintChange,
	revertData,
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<"customGroupAssetClassConstraints">> & {
	selectableUserClassifications: UserInstrumentClassificationDto[] | undefined;
	selectableAssetClasses: SelectableAllocationConstraintsResponse["selectableAssetClasses"] | undefined;
	editable: boolean;
	solvedWithAI: boolean;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<"customGroupAssetClassConstraints">;
}) {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const originalValues = revertData && {
		relation: revertData.relation,
		maxWeight: revertData.maxWeight ?? null,
		minWeight: revertData.minWeight ?? null,
	};
	const {
		applicationMode,
		tagType: _tagType,
		standardClassification: _standardClassification,
		classificationUuid,
		options,
		...common
	} = item;

	const { setValue, trigger } = useFormContext<StepPropsFor<"portfolioConstraints">["stepData"]>();
	const assetClassGroupLabel = useMemo(() => {
		if (options.length === 1) {
			return options[0];
		}

		if (options.length > 1) {
			return `Selected (${options.length} asset allocation tags)`;
		}

		return "Select values";
	}, [options]);
	const singleSelectedValue = options.length === 1 ? options[0] : undefined;
	const singleSelectedClassification = selectableUserClassifications?.find(
		(c) => c.classificationUuid === classificationUuid,
	);
	const singleSelectedColor = classificationOptionColor(singleSelectedClassification, singleSelectedValue);

	return (
		<>
			<div className="max-w-[180px] min-[1400px]:max-w-[300px] overflow-hidden pr-4">
				<div className={titleClassName}>{options.length > 0 ? "Asset Class Tag" : "Asset Class"}</div>
				<div>
					{item.readonly || !editable ? (
						singleSelectedValue ? (
							<TagBadge label={singleSelectedValue} color={singleSelectedColor} />
						) : (
							<EllipsisText text={assetClassGroupLabel} />
						)
					) : (
						<FormController
							control={control}
							name={`list.${index}.options`}
							render={({ fieldState, field }) => {
								const error = Boolean(fieldState.error?.message);
								const invalid = isInvalid({
									error,
									validity: item.validity,
									mode: solvedWithAI ? "ai" : "edit",
								});

								return (
									<AutoTooltip
										severity="error"
										position="left"
										mode="hover"
										disabled={!error}
										trigger={({ innerRef }) => (
											<ActionText
												data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/customCurrencyConstraint`}
												innerRef={innerRef}
												disabled={!selectableAssetClasses || !selectableUserClassifications}
												onClick={() =>
													selectableAssetClasses &&
													selectableUserClassifications &&
													spawnAssetAllocationDialog({
														classificationType: "TAG",
														initialValue: classificationUuid
															? { selectedOptions: options, classificationUuid }
															: undefined,
														mode: "checkbox",
														optionsProvider(selectedClassificationUuid) {
															return portfolioRepository.wizardAllocationSelectableClassifications.fetchQuery(
																ctx.area,
																{
																	classificationUuid: selectedClassificationUuid,
																	universeIdentifier: ctx.getStepsData().investableUniverse.universeIdentifier,
																},
															);
														},
														// disabledRowIds: getValues().list.flatMap((constraints, i) =>
														// 	i === index // self can always be changed
														// 		? []
														// 		: constraints.type === "assetClassGroupConstraints"
														// 		  ? constraints.assetClassesIdentifiers.map((assetClass) =>
														// 					generateAssetClassId(assetClass),
														// 		    )
														// 		  : constraints.type === "customGroupAssetClassConstraints"
														// 		    ? constraints.options.map((value) =>
														// 						userClassificationId(constraints.classificationUuid!, value),
														// 		      )
														// 		    : [],
														// ),
														selectableAssetClasses,
														selectableUserClassifications,
														onSubmit(data) {
															if (data.type === "RISK_MODEL") {
																setValue(`list.${index}`, {
																	...common,
																	type: "assetClassConstraints",
																	assetClassesIdentifiers: data.selectedOptions,
																} satisfies EditablePortfolioConstraintHelper<"assetClassConstraints">);
																trigger(`list.${index}.assetClassesIdentifiers`).catch(noop);
																onConstraintChange();
																return;
															}

															if (data.type === "TAG") {
																setValue(`list.${index}`, {
																	...common,
																	type: "customGroupAssetClassConstraints",
																	applicationMode,
																	options: data.selectedOptions,
																	classificationUuid: data.instrumentClassification!.classificationUuid!,
																	standardClassification:
																		data.instrumentClassification?.standardClassificationId ?? null,
																	tagType: "ASSET_CLASS",
																} satisfies EditablePortfolioConstraintHelper<"customGroupAssetClassConstraints">);
																trigger(`list.${index}.options`).catch(noop);
																onConstraintChange();
															}
														},
													})
												}
												className={{
													"w-full": true,
													[`!text-[color:var(--global-palette-danger-600)]`]: invalid,
												}}
											>
												{singleSelectedValue ? (
													<TagBadge label={singleSelectedValue} color={singleSelectedColor} />
												) : (
													<EllipsisText text={assetClassGroupLabel} className="text-left" />
												)}
											</ActionText>
										)}
									>
										{fieldState.error?.message}
									</AutoTooltip>
								);
							}}
						/>
					)}
				</div>
			</div>
			<ConstraintRelationBlock
				control={control}
				index={index}
				item={item}
				mode={item.readonly || !editable ? "read" : solvedWithAI ? "ai" : "edit"}
				originalValues={originalValues}
				onConstraintChange={onConstraintChange}
			/>
		</>
	);
}

const constraintCategoryToLabel: Record<ConstraintCategory, { label: string | ReactNode; tooltip: string }> = {
	assetClassConstraints: {
		label: "Asset allocation",
		tooltip:
			"Sets weights to different asset classes filtered by granularity, like a specific equity sector or geography",
	},
	instrumentMaxWeightConstraint: {
		label: "Max weights on single instrument",
		tooltip: "Set the maximum weights to be allocated to every single portfolio instrument",
	},
	tagConstraints: {
		label: "General tag",
		tooltip: "Sets weights to your already assigned universe tags.",
	},
	tagRelativeToTag: {
		label: "Tag relative to tag",
		tooltip: "Sets how much of one tag's allocation must be composed of instruments also belonging to another tag.",
	},
	currencyConstraints: {
		label: "Currency",
		tooltip: "Sets the currency for this portfolio.",
	},
	scoresConstraint: {
		label: "Score",
		tooltip: "Sets score to your instruments.",
	},
	lockInstrumentGroup: {
		label: "Lock instrument",
		tooltip: "This constraint will restrain the instrument in its current weight",
	},
	forEachConstraint: {
		label: "Single instrument constraint",
		tooltip: "set weights that will be applied to all the instruments in the selection",
	},
	customScoreConstraint: {
		label: "Score and metrics",
		tooltip: "Sets score to your instruments.",
	},
	customTagConstraint: {
		label: "General tag",
		tooltip: "Sets weights to your already assigned universe tags",
	},
	customAssetClassConstraint: {
		label: "Asset allocation",
		tooltip:
			"Sets weights to different asset classes filtered by granularity, like a specific equity sector or geography",
	},
	customCurrencyConstraint: {
		label: "Currency",
		tooltip: "Sets the currency for this portfolio.",
	},
	assetClassGroupConstraints: {
		label: "Group asset allocation",
		tooltip: "Sets weights to a single or groups of specific asset allocation.",
	},
	customGroupAssetClassConstraints: {
		label: "Group asset allocation",
		tooltip: "Sets weights to a single or groups of specific asset allocation.",
	},
	instrumentConstraints: {
		label: "Group instrument constraint",
		tooltip: "Set weights to a single instrument or groups of specific instruments.",
	},
	compositionPriority: {
		label: "Composition priority",
		tooltip: "Prioritises instruments already in the portfolio over new ones from the universe.",
	},
};

function PortfolioConstraintListItem({
	index,
	control,
	item,
	selectableData,
	editable,
	trigger,
	revertData,
	...forward
}: ConstraintSpecificColumnsProps<EditablePortfolioConstraintHelper<keyof PortfolioConstraintTypePayloadMap>> & {
	selectableData:
		| (SelectableAllocationConstraintsResponse & {
				selectableUserClassification?: UserInstrumentClassificationDto[];
		  })
		| undefined;
	editable: boolean;
	solvedWithAI: boolean;
	trigger(fieldName: string): void;
	onConstraintChange(): void;
	revertData?: EditablePortfolioConstraintHelper<keyof PortfolioConstraintTypePayloadMap>;
}): React.JSX.Element {
	const ctx = useMandatoryContext(EditPortfolioV4Context);
	const investableUniverseCtx = ctx.getStepsData().investableUniverse;
	return (
		<>
			<div className="flex mr-8 w-[140px]">
				<div className="flex-1">
					<div className={titleClassName}>Constraint</div>
					<div>
						<AutoTooltip
							position="left"
							overrideColor={themeCSSVars.global_palette_neutral_300}
							trigger={({ innerRef }) => (
								<span className="hover:font-medium hover:underline" ref={innerRef}>
									{constraintCategoryToLabel[constraintTypeToCategory[item.type]].label}
								</span>
							)}
						>
							<TooltipContent>
								<div className="font-bold mb-2">
									{constraintCategoryToLabel[constraintTypeToCategory[item.type]].label}
								</div>
								<div>{constraintCategoryToLabel[constraintTypeToCategory[item.type]].tooltip}</div>
							</TooltipContent>
						</AutoTooltip>
					</div>
				</div>
				<div>
					{forward.solvedWithAI && (
						<Icon className="ml-auto mt-[26px]" icon="Ask-ai" size={16} color={themeCSSVars.palette_graph_B500} />
					)}
				</div>
			</div>
			{(() => {
				switch (item.type) {
					case "assetClassConstraints":
						return (
							<AssetAllocationColumns
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"assetClassConstraints">
									>["control"]
								}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								selectableAssetClasses={selectableData?.selectableAssetClasses}
								editable={editable}
								trigger={trigger}
								revertData={revertData as EditablePortfolioConstraintHelper<"assetClassConstraints"> | undefined}
								{...forward}
							/>
						);
					case "instrumentConstraints":
						return (
							<InstrumentConstraintsColumns
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"instrumentConstraints">
									>["control"]
								}
								selectableInstruments={selectableData?.selectableInstruments}
								editable={editable}
								trigger={trigger}
								revertData={revertData as EditablePortfolioConstraintHelper<"instrumentConstraints"> | undefined}
								{...forward}
							/>
						);
					case "tagConstraints":
						return <></>; //TODO:Waiting for BE migration (enum variant should disappear)
					case "tagRelativeToTag":
						return (
							<TagRelativeToTagColumns
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"tagRelativeToTag">
									>["control"]
								}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								editable={editable}
								trigger={trigger}
								revertData={revertData as EditablePortfolioConstraintHelper<"tagRelativeToTag"> | undefined}
								{...forward}
							/>
						);
					case "lockInstrumentGroup":
						return (
							<LockInstrumentGroupColumn
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"lockInstrumentGroup">
									>["control"]
								}
								selectableLockableInstruments={selectableData?.selectableLockableInstruments}
								editable={editable}
								{...forward}
							/>
						);
					case "forEachConstraint":
						return (
							<ForEachConstraintColumn
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"forEachConstraint">
									>["control"]
								}
								selectableInstruments={selectableData?.selectableInstruments}
								editable={editable}
								trigger={trigger}
								revertData={revertData as EditablePortfolioConstraintHelper<"forEachConstraint"> | undefined}
								{...forward}
							/>
						);
					case "instrumentMaxWeightConstraint":
						return (
							<InstrumentMaxWeightColumn
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"instrumentMaxWeightConstraint">
									>["control"]
								}
								editable={editable}
								trigger={trigger}
								revertData={
									revertData as EditablePortfolioConstraintHelper<"instrumentMaxWeightConstraint"> | undefined
								}
								{...forward}
							/>
						);
					case "scoresConstraint":
						return <></>; //TODO: Waiting for BE migration (enum variant should disappear)
					case "currencyConstraints":
						return (
							<CurrencyColumn
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"currencyConstraints">
									>["control"]
								}
								editable={editable}
								trigger={trigger}
								selectableCurrencies={selectableData?.selectableCurrencies}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								revertData={revertData as EditablePortfolioConstraintHelper<"currencyConstraints"> | undefined}
								{...forward}
							/>
						);
					case "customCurrencyConstraint":
						return (
							<CustomCurrencyConstraint
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"customCurrencyConstraint">
									>["control"]
								}
								editable={editable}
								trigger={trigger}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								selectableCurrencies={selectableData?.selectableCurrencies}
								revertData={revertData as EditablePortfolioConstraintHelper<"customCurrencyConstraint"> | undefined}
								{...forward}
							/>
						);
					case "customAssetClassConstraint":
						return (
							<CustomAssetClassConstraint
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"customAssetClassConstraint">
									>["control"]
								}
								editable={editable}
								trigger={trigger}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								selectableAssetClasses={selectableData?.selectableAssetClasses}
								revertData={revertData as EditablePortfolioConstraintHelper<"customAssetClassConstraint"> | undefined}
								{...forward}
							/>
						);
					case "customTagConstraint":
						return (
							<CustomTagConstraint
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"customTagConstraint">
									>["control"]
								}
								editable={editable}
								trigger={trigger}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								revertData={revertData as EditablePortfolioConstraintHelper<"customTagConstraint"> | undefined}
								{...forward}
							/>
						);
					case "customScoreConstraint":
						return (
							<CustomScoreConstraint
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"customScoreConstraint">
									>["control"]
								}
								editable={editable}
								trigger={trigger}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								revertData={revertData as EditablePortfolioConstraintHelper<"customScoreConstraint"> | undefined}
								{...forward}
							/>
						);
					case "assetClassGroupConstraints":
						return (
							<AssetAllocationColumnsGroup
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"assetClassGroupConstraints">
									>["control"]
								}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								selectableAssetClasses={selectableData?.selectableAssetClasses}
								editable={editable}
								revertData={revertData as EditablePortfolioConstraintHelper<"assetClassGroupConstraints"> | undefined}
								{...forward}
							/>
						);
					case "compositionPriority":
						return <div className="flex-1" />;
					case "customGroupAssetClassConstraints":
						return (
							<CustomGroupAssetAllocationColumns
								index={index}
								item={item}
								control={
									control as ConstraintSpecificColumnsProps<
										EditablePortfolioConstraintHelper<"customGroupAssetClassConstraints">
									>["control"]
								}
								selectableUserClassifications={selectableData?.selectableUserClassification}
								selectableAssetClasses={selectableData?.selectableAssetClasses}
								editable={editable}
								revertData={
									revertData as EditablePortfolioConstraintHelper<"customGroupAssetClassConstraints"> | undefined
								}
								{...forward}
							/>
						);
					default:
						unreachable(item);
				}
			})()}
			{item.type !== "lockInstrumentGroup" && (
				<div className="flex space-x-4 ml-auto">
					<div className="">
						<div className={titleClassName}>Soft constraint</div>
						<div className="flex justify-start">
							<FormController
								control={control}
								name={`list.${index}.target`}
								render={({ field: { ref: fieldRef, onChange, value, onBlur } }) => {
									// compositionPriority is always a soft constraint: force checked and non-editable.
									const forcedSoftConstraint = item.type === "compositionPriority";
									return (
										<Checkbox
											data-qualifier={`portfolioWizard/portfolioConstraints/constraint(${item.type},${index})/target`}
											disabled={
												forcedSoftConstraint ||
												item.readonly ||
												!editable ||
												!canSetSoftConstraint(item, investableUniverseCtx)
											}
											checked={forcedSoftConstraint ? true : value}
											innerRef={fieldRef}
											onChange={(target) => {
												onChange(target);
												onBlur();
											}}
										/>
									);
								}}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

/** @internal — exported for tests */
export function canSetSoftConstraint(
	item: EditablePortfolioConstraintHelper<keyof PortfolioConstraintTypePayloadMap>,
	universeStep: InvestableUniverseStep,
) {
	if (universeStep.type === "GROUP_UNIVERSE") {
		return (
			item.type !== "lockInstrumentGroup" &&
			item.type !== "instrumentMaxWeightConstraint" &&
			item.type !== "compositionPriority" &&
			item.relation === "RELATIVE_TO_REFERENCE"
		);
	}

	return true;
}

//#endregion

function ReadonlyRelation(props: {
	label?: "MAX" | "MIN" | "WEIGHT" | "RANGE" | "APPLY_TO.PLACEHOLDER" | "OPERATOR";
	children: NodeOrFn;
	className?: ClassList;
	align?: "right" | "left";
}) {
	const { t } = useTranslation();
	const { align = "left" } = props;
	return (
		<div className={toClassName(props.className)}>
			<Text
				type="Body/M/Book"
				as="p"
				className={{
					"pb-2": true,
					"h-[26px]": !props.label,
					"text-right": align === "right",
					"text-left": align === "left",
				}}
			>
				{props.label ? t(props.label) : ""}
			</Text>
			{renderNodeOrFn(props.children)}
		</div>
	);
}

/** @internal — exported for tests */
export function applyAISolution(el: EditablePortfolioConstraint): MaybeArray<EditablePortfolioConstraint> {
	switch (el.type) {
		case "assetClassConstraints":
		case "instrumentConstraints":
		case "tagRelativeToTag":
		case "assetClassGroupConstraints":
		case "customGroupAssetClassConstraints":
		case "tagConstraints":
		case "currencyConstraints":
			return {
				...el,
				minWeight: el.suggestedMinWeight,
				maxWeight: el.suggestedMaxWeight,
				validity: ConstraintValidity.Invalid,
			};
		case "instrumentMaxWeightConstraint":
			return {
				...el,
				weight: el.suggestedMaxWeight,
				validity: ConstraintValidity.Invalid,
			};
		case "scoresConstraint":
			return {
				...el,
				minWeight: el.suggestedMinWeight,
				maxWeight: el.suggestedMaxWeight,
				type: "scoresConstraint",
				instrumentWithouthScorePolicy: enumInstrumentWithouthScorePolicy.Exclude,
				validity: ConstraintValidity.Invalid,
			};
		case "forEachConstraint": {
			if (el.tickerIdentifiers.length === 1) {
				return [
					{
						...el,
						maxWeight: el.tickerIdentifiers[0]?.suggestedMaxWeight ?? el.maxWeight,
						minWeight: el.tickerIdentifiers[0]?.suggestedMinWeight ?? el.minWeight,
						suggestedMaxWeight: el.tickerIdentifiers[0]?.suggestedMaxWeight,
						suggestedMinWeight: el.tickerIdentifiers[0]?.suggestedMinWeight,
						validity: ConstraintValidity.Invalid,
					},
					// satisfies EditablePortfolioConstraintHelper<"forEachConstraint">
				];
			}

			const unfeasibleConstraints = el.tickerIdentifiers.filter((x) => !x.feasible);
			const splittedConstraint: Array<EditablePortfolioConstraintHelper<"forEachConstraint">> =
				unfeasibleConstraints.map((x) => ({
					id: generateUniqueDOMId(),
					type: el.type,
					identifier: null,
					tickerIdentifiers: [x],
					maxWeight: x.suggestedMaxWeight ?? null,
					minWeight: x.suggestedMinWeight ?? null,
					relation: el.relation ?? "BETWEEN",
					suggestedMaxWeight: x.suggestedMaxWeight ?? null,
					suggestedMinWeight: x.suggestedMinWeight ?? null,
					validity: ConstraintValidity.Invalid,
					target: false,
					readonly: false,
				}));

			return [
				{
					...el,
					tickerIdentifiers: el.tickerIdentifiers.map((x) => ({
						...x,
						deleted: !x.feasible,
					})),
					validity: ConstraintValidity.Invalid,
				},
				...splittedConstraint,
			];
		}
		case "lockInstrumentGroup": {
			const unfeasibleConstraints = el.tickerIdentifiers.filter((x) => !x.feasible);
			const splittedConstraint = unfeasibleConstraints.map(
				(x): EditablePortfolioConstraintHelper<"forEachConstraint"> => ({
					id: generateUniqueDOMId(),
					type: "forEachConstraint",
					identifier: null,
					tickerIdentifiers: [x],
					maxWeight: x.suggestedMaxWeight ?? null,
					minWeight: x.suggestedMinWeight ?? null,
					relation: el.relation,
					suggestedMaxWeight: x.suggestedMaxWeight,
					suggestedMinWeight: x.suggestedMinWeight,
					validity: ConstraintValidity.Invalid,
					target: false,
					readonly: false,
				}),
			);

			return [
				{
					...el,
					tickerIdentifiers: el.tickerIdentifiers.map((x) => ({
						...x,
						deleted: !x.feasible,
					})),
					validity: ConstraintValidity.Invalid,
				},
				...splittedConstraint,
			];
		}
		case "customScoreConstraint":
			return {
				...el,
				minWeight: el.suggestedMinWeight ?? null,
				maxWeight: el.suggestedMaxWeight ?? null,
				validity: ConstraintValidity.Invalid,
			};
		case "customTagConstraint":
			return {
				...el,
				minWeight: el.suggestedMinWeight ?? null,
				maxWeight: el.suggestedMaxWeight ?? null,
				validity: ConstraintValidity.Invalid,
			};
		case "customAssetClassConstraint":
			return {
				...el,
				minWeight: el.suggestedMinWeight ?? null,
				maxWeight: el.suggestedMaxWeight ?? null,
				validity: ConstraintValidity.Invalid,
			};
		case "customCurrencyConstraint":
			return {
				...el,
				minWeight: el.suggestedMinWeight ?? null,
				maxWeight: el.suggestedMaxWeight ?? null,
				validity: ConstraintValidity.Invalid,
			};
		case "compositionPriority":
			return { ...el, validity: ConstraintValidity.Invalid };
		default:
			unreachable(el);
	}
}
// debt
/** @internal — exported for tests */
export function remapConstraintCategory(type: ConstraintCategory): ConstraintCategory {
	if (type === "customAssetClassConstraint") {
		return "assetClassConstraints";
	}

	if (type === "customGroupAssetClassConstraints") {
		return "assetClassGroupConstraints";
	}

	if (type === "customCurrencyConstraint") {
		return "currencyConstraints";
	}

	return type;
}
